import { useContext, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';

// ---------------------------------------------------------------------------
// Supported languages
// ---------------------------------------------------------------------------
export const SUPPORTED_LANGS = ['de', 'en', 'es'];
export const DEFAULT_LANG = 'de';

// ---------------------------------------------------------------------------
// Translation dictionaries
// ---------------------------------------------------------------------------
const translations = {
  // =========================================================================
  // GERMAN
  // =========================================================================
  de: {
    // --- Languages ---
    'lang.de': 'Deutsch',
    'lang.en': 'English',
    'lang.es': 'Español',

    // --- Common actions ---
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.edit': 'Bearbeiten',
    'common.create': 'Erstellen',
    'common.add': 'Hinzufügen',
    'common.close': 'Schließen',
    'common.back': '← Zurück',
    'common.loading': 'Lädt...',
    'common.saving': 'Speichern...',
    'common.deleting': 'Löschen...',
    'common.search': 'Suchen...',
    'common.filter': 'Filtern',
    'common.reset': 'Zurücksetzen',
    'common.columns': 'Spalten',
    'common.confirm': 'Bestätigen',
    'common.yes': 'Ja',
    'common.no': 'Nein',
    'common.ok': 'OK',
    'common.apply': 'Anwenden',
    'common.export': 'Exportieren',
    'common.import': 'Importieren',
    'common.download': 'Herunterladen',
    'common.upload': 'Hochladen',
    'common.rename': 'Umbenennen',
    'common.duplicate': 'Duplizieren',
    'common.preview': 'Vorschau',
    'common.publish': 'Veröffentlichen',
    'common.archive': 'Archivieren',
    'common.activate': 'Aktivieren',
    'common.deactivate': 'Deaktivieren',
    'common.enable': 'Aktivieren',
    'common.disable': 'Deaktivieren',
    'common.optional': 'optional',
    'common.required': 'Pflichtfeld',
    'common.all': 'Alle',
    'common.none': 'Keine',
    'common.or': 'oder',
    'common.and': 'und',
    'common.by': 'von',
    'common.at': 'um',
    'common.on': 'am',
    'common.from': 'von',
    'common.to': 'bis',
    'common.of': 'von',
    'common.items': 'Einträge',
    'common.actions': 'Aktionen',
    'common.status': 'Status',
    'common.type': 'Typ',
    'common.name': 'Name',
    'common.title': 'Titel',
    'common.description': 'Beschreibung',
    'common.notes': 'Notizen',
    'common.date': 'Datum',
    'common.time': 'Uhrzeit',
    'common.email': 'E-Mail',
    'common.phone': 'Telefon',
    'common.address': 'Adresse',
    'common.image': 'Bild',
    'common.images': 'Bilder',
    'common.file': 'Datei',
    'common.files': 'Dateien',
    'common.size': 'Größe',
    'common.color': 'Farbe',
    'common.number': '#',
    'common.total': 'Gesamt',
    'common.count': 'Anzahl',
    'common.yes_delete': 'Ja, löschen',
    'common.confirm_delete_title': 'Bist du sicher?',
    'common.confirm_delete': 'Wirklich löschen?',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.warning': 'Warnung',
    'common.info': 'Info',
    'common.no_access': 'Kein Zugriff',
    'common.no_access_desc': 'Du hast keine Berechtigung, diese Seite zu sehen.',
    'common.not_found': 'Nicht gefunden',
    'common.to_home': 'Zur Startseite',
    'common.copy': 'Kopieren',
    'common.copied': 'Kopiert!',
    'common.share': 'Teilen',
    'common.link': 'Link',
    'common.read_more': 'Mehr lesen',
    'common.show_less': 'Weniger anzeigen',

    // --- Roles ---
    'role.admin': 'Admin',
    'role.coach': 'Coach',
    'role.social_media': 'Social Media',
    'role.staff': 'Mitarbeiter',
    'role.viewer': 'Zuschauer',
    'role.show_master': 'Show Master',
    'role.tournament_manager': 'Turnierleitung',
    'role.superadmin': 'Superadmin',

    // --- Module names ---
    'module.news': 'Neuigkeiten',
    'module.teams': 'Teams',
    'module.tournament': 'Turnier',
    'module.registration': 'Anmeldung',
    'module.flyer': 'Downloads',
    'module.finance': 'Finanzen',
    'module.teamtimes': 'Teamtimes',
    'module.lists': 'Listen',
    'module.files': 'Dateien',
    'module.todos': 'ToDos',
    'module.meals': 'Küche',
    'module.staff': 'Mitarbeiter',
    'module.equipment': 'Equipment',
    'module.checkin': 'Check-in',
    'module.schedule': 'Tagesplan',
    'module.evenings': 'Abende',
    'module.questions': 'Frage des Tages',
    'module.statistics': 'Statistiken',
    'module.forms': 'Formulare',
    'module.settings': 'Einstellungen',

    // --- Sidebar nav ---
    'nav.dashboard': 'Dashboard',
    'nav.section.camp': 'CAMP',
    'nav.section.program': 'PROGRAMM',
    'nav.section.org': 'ORGANISATION',
    'nav.section.admin': 'ADMIN',
    'nav.news': 'Neuigkeiten',
    'nav.participants': 'Teilnehmer',
    'nav.teams': 'Teams',
    'nav.attendance': 'Anwesenheit',
    'nav.checkin': 'Check-in',
    'nav.tournament': 'Turnier',
    'nav.schedule': 'Tagesplan',
    'nav.evenings': 'Abende',
    'nav.teamtimes': 'Teamtimes',
    'nav.questions': 'Frage d. Tages',
    'nav.staff': 'Mitarbeiter',
    'nav.todos': 'ToDos',
    'nav.lists': 'Listen',
    'nav.files': 'Dateien',
    'nav.meals': 'Küche',
    'nav.finance': 'Finanzen',
    'nav.equipment': 'Equipment',
    'nav.downloads': 'Downloads',
    'nav.statistics': 'Statistiken',
    'nav.forms': 'Formulare',
    'nav.emails': 'E-Mail',
    'nav.camp_settings': 'Camp-Einstellungen',
    'nav.to_public': 'Zur öffentlichen Seite',
    'nav.logout': 'Abmelden',
    'nav.expand': 'Ausklappen',
    'nav.collapse': 'Einklappen',
    'nav.public_label': 'Öffentlich',
    'nav.intern_area': 'Intern-Bereich',

    // --- Layout / App shell strings ---
    'layout.active': 'Aktiv',
    'layout.archive': 'Archiv',
    'layout.archive_banner': 'Archiv-Ansicht — {name} ist nicht das aktive Camp. Keine Daten werden verändert.',
    'layout.to_active_camp': 'Zum aktiven Camp →',
    'layout.simulation': 'Simulation',
    'layout.simulation_banner': 'Simulation: {name}',
    'layout.end_simulation': 'Simulation beenden',
    'layout.camp_not_found': 'Camp nicht gefunden',
    'layout.loading': 'Laden...',
    'layout.menu': 'Menü',
    'layout.to_platform': 'Zur Plattform',
    'layout.to_camp': 'Zur Camp-Seite',
    'layout.login': 'Anmelden',
    'layout.intern': 'Intern',
    'layout.datenschutz': 'Datenschutz',

    // --- Public nav ---
    'pub.home': 'Home',
    'pub.news': 'News',
    'pub.teams': 'Teams',
    'pub.tournament': 'Turnier',
    'pub.evenings': 'Abende',
    'pub.registration': 'Anmeldung',
    'pub.downloads': 'Downloads',

    // --- Auth ---
    'auth.login_title': 'Anmelden',
    'auth.username_or_email': 'Benutzername oder E-Mail',
    'auth.password': 'Passwort',
    'auth.login_btn': 'Anmelden',
    'auth.logging_in': 'Anmelden...',
    'auth.login_failed': 'Anmeldung fehlgeschlagen',
    'auth.logout': 'Abmelden',
    'auth.camp_login': 'Camp-Login',
    'auth.back_to_site': 'Zurück zur Camp-Seite',
    'auth.password_hint': 'Passwort (leer lassen = unverändert)',
    'auth.register_link': 'Noch kein Account? Jetzt registrieren',
    'auth.register_title': 'Account anfragen',
    'auth.register_subtitle': 'Dein Account muss von der Campleitung freigeschaltet werden.',
    'auth.display_name': 'Name / Anzeigename',
    'auth.register_message': 'Nachricht an die Campleitung (optional)',
    'auth.register_btn': 'Anfrage senden',
    'auth.registering': 'Wird gesendet...',
    'auth.register_success_title': 'Anfrage eingereicht',
    'auth.register_success_body': 'Die Campleitung wird deinen Account prüfen und freischalten.',
    'auth.back_to_login': 'Zurück zum Login',
    'auth.pending_approval': 'Dein Account muss noch von der Campleitung aktiviert werden.',
    'profile.title': 'Mein Profil',
    'profile.account_info': 'Account-Informationen',
    'profile.display_name': 'Anzeigename',
    'profile.username': 'Benutzername',
    'profile.email': 'E-Mail',
    'profile.role': 'Rolle',
    'profile.member_since': 'Mitglied seit',
    'profile.save': 'Änderungen speichern',
    'profile.saving': 'Wird gespeichert...',
    'profile.saved': 'Gespeichert',
    'profile.change_password': 'Passwort ändern',
    'profile.current_password': 'Aktuelles Passwort',
    'profile.new_password': 'Neues Passwort',
    'profile.confirm_password': 'Passwort bestätigen',
    'profile.password_mismatch': 'Passwörter stimmen nicht überein',
    'profile.password_min': 'Mindestens 8 Zeichen',
    'profile.update_password': 'Passwort aktualisieren',
    'profile.updating': 'Wird aktualisiert...',
    'profile.updated': 'Passwort aktualisiert',

    // --- Dashboard ---
    'dashboard.to_camp_site': 'Zur Camp-Seite',
    'dashboard.participants': 'Teilnehmer',
    'dashboard.checked_in': 'Eingecheckt',
    'dashboard.teams': 'Teams',
    'dashboard.paid': 'Bezahlt',
    'dashboard.att_today': 'Heute anwesend',
    'dashboard.open_todos': 'Offene Todos',
    'dashboard.settings': 'Einstellungen',

    // --- News ---
    'news.title': 'Neuigkeiten',
    'news.subtitle': 'Aktuelle Infos, Berichte und Ankündigungen rund um das Camp.',
    'news.create': '+ Beitrag erstellen',
    'news.edit': 'Beitrag bearbeiten',
    'news.new': 'Neuer Beitrag',
    'news.no_news': 'Noch keine Neuigkeiten',
    'news.no_news_desc': 'Schau später wieder vorbei.',
    'news.read_more': 'Mehr lesen',
    'news.read': 'Lesen',
    'news.pinned': 'ANGEHEFTET',
    'news.featured': 'HIGHLIGHT',
    'news.by': 'von',
    'news.images': 'Bilder',
    'news.more_news': 'Weitere Neuigkeiten',
    'news.back': 'Zurück zu Neuigkeiten',
    'news.not_found': 'Artikel nicht gefunden',
    'news.not_found_desc': 'Dieser Beitrag existiert nicht oder wurde archiviert.',
    'news.share': 'Teilen:',
    'news.copy_link': 'Link kopieren',
    'news.copied': 'Kopiert!',
    'news.article_count': '{count} Beiträge gesamt',
    'news.filter_all': 'Alle',
    'news.pin': 'Anheften',
    'news.unpin': 'Loslösen',
    'news.set_featured': 'Als Highlight setzen',
    'news.set_homepage': 'Auf Startseite',
    'news.slug_label': 'URL-Slug',
    'news.slug_hint': 'Wird automatisch generiert',
    'news.teaser': 'Teaser / Vorschautext',
    'news.content': 'Inhalt',
    'news.hero_image': 'Titelbild',
    'news.gallery': 'Bildergalerie',
    'news.gallery_hint': 'Bilder per Drag & Drop sortieren',
    'news.seo_title': 'SEO Titel',
    'news.seo_description': 'SEO Beschreibung',
    'news.tags': 'Tags',
    'news.publish_at': 'Veröffentlichen am',
    'news.expires_at': 'Ablaufdatum',
    'news.duplicate': 'Duplizieren',
    'news.status_change': 'Status ändern',
    'news.delete_confirm': 'Diesen Beitrag wirklich löschen?',
    'news.lang_tab_hint': 'Wechsle die Sprache der Textfelder. Bilder und Metadaten bleiben sprachübergreifend gleich.',

    // News status
    'news.status.draft': 'Entwurf',
    'news.status.scheduled': 'Geplant',
    'news.status.published': 'Veröffentlicht',
    'news.status.archived': 'Archiviert',

    // News categories
    'news.cat.general': 'Allgemein',
    'news.cat.registration': 'Anmeldung',
    'news.cat.tournament': 'Turnier',
    'news.cat.program': 'Programm',
    'news.cat.teams': 'Teams',
    'news.cat.staff': 'Mitarbeiter',
    'news.cat.parents': 'Eltern',
    'news.cat.reminder': 'Erinnerung',
    'news.cat.recap': 'Rückblick',
    'news.cat.important': 'Wichtig',

    // News admin list
    'news.admin.title': 'News verwalten',
    'news.admin.all': 'Alle',
    'news.admin.drafts': 'Entwürfe',
    'news.admin.published': 'Veröffentlicht',
    'news.admin.scheduled': 'Geplant',
    'news.admin.archived': 'Archiviert',
    'news.admin.no_results': 'Keine Beiträge gefunden',
    'news.admin.search': 'Beiträge suchen...',

    // --- Participants ---
    'participants.title': 'Teilnehmer',
    'participants.add': '+ Teilnehmer',
    'participants.search': 'Teilnehmer suchen...',
    'participants.import_csv': '📤 CSV Import',
    'participants.first_name': 'Vorname',
    'participants.last_name': 'Nachname',
    'participants.jersey': 'Trikotnummer',
    'participants.age': 'Alter',
    'participants.team': 'Team',
    'participants.all_teams': 'Alle Teams',
    'participants.position': 'Position',
    'participants.experience': 'Erfahrung',
    'participants.gender': 'Geschlecht',
    'participants.notes': 'Notizen',
    'participants.edit': 'Teilnehmer bearbeiten',
    'participants.new': 'Neuer Teilnehmer',
    'participants.delete_confirm': 'Teilnehmer wirklich löschen?',
    'participants.no_results': 'Keine Teilnehmer gefunden',
    'participants.total': '{count} Teilnehmer',
    'participants.count': '{filtered} von {total} Teilnehmern',
    'participants.payment': 'Zahlung',
    'participants.registration_status': 'Anmeldestatus',
    'participants.diet': 'Ernährung',
    'participants.add_btn': 'Hinzufügen',
    'participants.set_status': 'Status setzen',
    'participants.set_payment': 'Zahlung setzen',
    'participants.assign_team': 'Team zuweisen',
    'participants.no_team': 'Kein Team',
    'participants.grid_edit': 'Rasteransicht bearbeiten',
    'participants.end_grid_edit': 'Bearbeitung beenden',
    'participants.selected': '{n} ausgewählt',
    'participants.actions': 'Aktionen',
    'participants.page_of': 'Seite {page} von {total}',
    'participants.save_view': 'Ansicht speichern',
    'participants.view_name': 'Name der Ansicht',
    'participants.add_modal_title': 'Teilnehmer hinzufügen',
    'participants.personal_data': 'Persönliche Daten',
    'participants.baseball': 'Baseball',
    'participants.contact': 'Kontakt',
    'participants.health': 'Gesundheit',
    'participants.status_payment': 'Status & Zahlung',
    'participants.internal_notes': 'Interne Notizen',
    'participants.notes_placeholder': 'Nur für Mitarbeiter sichtbar…',
    'participants.first_name_label': 'Vorname',
    'participants.last_name_label': 'Nachname',
    'participants.email': 'E-Mail',
    'participants.phone': 'Telefon',
    'participants.dob': 'Geburtsdatum',
    'participants.shirt': 'T-Shirt',
    'participants.club': 'Verein',
    'participants.play_position': 'Spielposition',
    'participants.level': 'Level',
    'participants.height': 'Größe (cm)',
    'participants.weight': 'Gewicht (kg)',
    'participants.emergency_contact': 'Notfallkontakt Name',
    'participants.emergency_phone': 'Notfallkontakt Tel.',
    'participants.allergies': 'Allergien',
    'participants.nutrition': 'Ernährung',
    'participants.medical': 'Medizinische Hinweise',
    'participants.parent_name': 'Elternteil',
    'participants.parent_phone': 'Eltern Tel.',
    'participants.parent_email': 'Eltern E-Mail',
    'participants.amount': 'Betrag (€)',
    'participants.amount_paid': 'Bezahlt (€)',
    'participants.date': 'Datum',
    'participants.delete_confirm_bulk': '{n} Teilnehmer wirklich löschen?',
    'participants.delete_confirm_single': 'Teilnehmer wirklich löschen?',
    // Registration status labels
    'participants.status.new': 'Neu angemeldet',
    'participants.status.incomplete': 'Unvollständig',
    'participants.status.confirmed': 'Bestätigt',
    'participants.status.waitlist': 'Warteliste',
    'participants.status.cancelled': 'Storniert',
    'participants.status.checked_in': 'Eingecheckt',
    'participants.status.departed': 'Abgereist',
    // Payment status labels
    'participants.payment.open': 'Offen',
    'participants.payment.partial': 'Teil-bezahlt',
    'participants.payment.paid': 'Bezahlt',
    'participants.payment.discounted': 'Rabattiert',
    'participants.payment.free': 'Freiplatz',
    'participants.payment.cash': 'Bar bezahlt',
    'participants.payment.transfer': 'Überwiesen',
    // Gender labels
    'participants.gender.male': 'Männlich',
    'participants.gender.female': 'Weiblich',
    'participants.gender.other': 'Divers',
    // Experience labels
    'participants.exp.beginner': 'Anfänger',
    'participants.exp.intermediate': 'Fortgeschritten',
    'participants.exp.advanced': 'Fortgeschritten+',
    'participants.exp.pro': 'Profi',

    // Registration status
    'reg.status.registered': 'Angemeldet',
    'reg.status.confirmed': 'Bestätigt',
    'reg.status.waitlist': 'Warteliste',
    'reg.status.cancelled': 'Abgesagt',

    // Payment
    'payment.open': 'Offen',
    'payment.paid': 'Bezahlt',
    'payment.partial': 'Teilweise',
    'payment.refunded': 'Erstattet',

    // Gender
    'gender.male': 'Männlich',
    'gender.female': 'Weiblich',
    'gender.diverse': 'Divers',
    'gender.unknown': 'Unbekannt',

    // Experience
    'exp.beginner': 'Anfänger',
    'exp.intermediate': 'Fortgeschritten',
    'exp.advanced': 'Erfahren',
    'exp.elite': 'Elite',

    // --- Teams ---
    'teams.title': 'Teams',
    'teams.add': '+ Team hinzufügen',
    'teams.new': 'Neues Team',
    'teams.edit': 'Team bearbeiten',
    'teams.name': 'Teamname',
    'teams.color': 'Teamfarbe',
    'teams.timeslot': 'Zeitslot',
    'teams.logo': 'Logo',
    'teams.photo': 'Teamfoto',
    'teams.photo_optional': 'Teamfoto (optional)',
    'teams.description': 'Beschreibung (optional)',
    'teams.no_teams': 'Keine Teams vorhanden',
    'teams.coach': 'Coach',
    'teams.players': 'Spieler',
    'teams.no_players': 'Keine Spieler zugewiesen',
    'teams.morning': 'Vormittag',
    'teams.afternoon': 'Nachmittag',
    'teams.cheerleader': 'Cheerleader',
    'teams.players_tab': 'Spieler',
    'teams.attendance_tab': 'Anwesenheit',
    'teams.delete_confirm': 'Team wirklich löschen?',
    'teams.delete_confirm_full': 'Team wirklich löschen? Teilnehmer werden keinem Team zugewiesen.',
    'teams.subtitle': '{count} Teams · {participants} Teilnehmer',
    'teams.tab_manage': 'Teams',
    'teams.tab_assign': 'Zuteilung',
    'teams.tab_balance': 'Balance',
    'teams.create_btn': 'Team erstellen',
    'teams.none_yet': 'Noch keine Teams',
    'teams.none_yet_desc': 'Erstelle das erste Team!',
    'teams.avg_age': 'Ø Alter',
    'teams.avg_height': 'Ø cm',
    'teams.coaches': 'Coaches',
    'teams.error_load': 'Fehler beim Laden',
    'teams.error_save': 'Fehler beim Speichern',
    'teams.modal_create': 'Team erstellen',
    'teams.modal_edit': 'Team bearbeiten',
    'teams.photo_hero': 'Teamfoto (Hero)',
    'teams.search_coach': 'Person suchen…',
    'teams.no_coach_result': 'Kein Ergebnis',
    'teams.auto_assign': 'Auto-Zuteilung',
    'teams.save_changes': 'Änderungen speichern',
    'teams.create_first': 'Erstelle zunächst Teams im Tab "Teams".',
    'teams.no_team_label': 'Ohne Team',
    'teams.search_participant': 'Teilnehmer suchen…',
    'teams.all_assigned': 'Alle zugeteilt',
    'teams.no_results_search': 'Keine Treffer',
    'teams.drop_hint': 'Teilnehmer zum Zuweisen hier rein ziehen',
    'teams.no_data': '(keine Daten)',
    'teams.calc': 'Berechne…',
    'teams.distribute': 'Verteile…',
    'teams.distribute_n': '{n} Teilnehmer verteilen',
    'teams.no_teams_yet': 'Noch keine Teams vorhanden.',
    'teams.slot_morning': 'Vormittag',
    'teams.slot_afternoon': 'Nachmittag',
    'teams.unassigned_warning': '{n} Teilnehmer noch keinem Team zugeteilt.',
    'teams.criterion_age': 'Alter',
    'teams.criterion_height': 'Größe',
    'teams.criterion_weight': 'Gewicht',
    'teams.criterion_experience': 'Erfahrung',
    'teams.criterion_gender': 'Geschlecht',
    'teams.auto_desc': '{n} Teilnehmer auf {teams} Teams verteilen. Wähle Kriterien für eine ausgewogene Verteilung (Snake-Draft):',
    'teams.tn': 'TN',
    'teams.no_members': 'Keine Spieler in diesem Team',
    'teams.col_year': 'Jahrgang',
    'teams.col_shirt': 'Shirt',
    'teams.choose_image': 'Bild wählen',
    'teams.logo_upload': 'Logo',

    // --- Attendance ---
    'att.title': 'Anwesenheit',
    'att.present': 'Anwesend',
    'att.absent': 'Abwesend',
    'att.late': 'Verspätet',
    'att.excused': 'Entschuldigt',
    'att.not_recorded': '— Nicht erfasst',
    'att.click_hint': 'Klicke auf den Status um ihn zu wechseln',
    'att.date': 'Datum',
    'att.all_present': 'Alle anwesend',
    'att.overview': 'Übersicht',

    // --- Check-in ---
    'checkin.title': 'Check-in',
    'checkin.search': 'Teilnehmer suchen...',
    'checkin.checked_in': 'Eingecheckt',
    'checkin.not_checked_in': 'Nicht eingecheckt',
    'checkin.all': 'Alle',
    'checkin.check_in': 'Einchecken',
    'checkin.check_out': 'Auschecken',
    'checkin.no_results': 'Keine Ergebnisse',
    'checkin.total': '{checked}/{total} eingecheckt',

    // --- Tournament ---
    'tourn.title': 'Turnier',
    'tourn.add_match': '+ Spiel hinzufügen',
    'tourn.no_matches': 'Kein Turnierplan vorhanden',
    'tourn.live_matches': 'LIVE SPIELE',
    'tourn.scheduled': 'Geplant',
    'tourn.live': 'LIVE',
    'tourn.finished': 'Beendet',
    'tourn.field': 'Feld',
    'tourn.round': 'Runde',
    'tourn.edit_score': 'Score bearbeiten',
    'tourn.live_score': 'Live Score',
    'tourn.score_status': 'Score / Status',
    'tourn.add_match_title': 'Spiel hinzufügen',
    'tourn.edit_match_title': 'Spiel bearbeiten',
    'tourn.team1': 'Team 1',
    'tourn.team2': 'Team 2',
    'tourn.tbd': '— TBD —',
    'tourn.sort_order': 'Sortierung',
    'tourn.delete_confirm': 'Spiel wirklich löschen?',
    'tourn.vs': 'vs.',
    'tourn.live_ticker': 'Live-Ticker',
    'tourn.no_matches_title': 'Noch keine Spiele',
    'tourn.no_matches_desc': 'Generiere einen Round-Robin oder füge einzelne Spiele hinzu.',
    'tourn.generate_btn': 'Round-Robin generieren',
    'tourn.match_short': 'Spiel',
    'tourn.generating': 'Generiere…',
    'tourn.generate_confirm': 'Alle bestehenden Spiele löschen und Round-Robin neu generieren?',
    'tourn.generated_msg': '{count} Spiele in {rounds} Runden generiert.',
    'tourn.generate_error': 'Fehler beim Generieren',
    'tourn.tab_standings': 'Tabelle',
    'tourn.tab_schedule': 'Spielplan',
    'tourn.tab_bracket': 'Bracket',
    'tourn.tab_results': 'Ergebnisse',
    'tourn.no_matches_empty': 'Keine Spiele',
    'tourn.no_results': 'Noch keine Ergebnisse',
    'tourn.round_n': 'Runde {n}',
    'tourn.matches_count': '{n} Spiele',
    'tourn.col_games': 'Sp',
    'tourn.col_wins': 'S',
    'tourn.col_ties': 'U',
    'tourn.col_losses': 'N',
    'tourn.col_points': 'Punkte',
    'tourn.field_n': 'Feld {n}',
    'tourn.ago_now': 'gerade',
    'tourn.ago_min': 'vor {n}m',
    'tourn.ago_hour': 'vor {n}h',
    'tourn.home_team': 'Heim-Team',
    'tourn.away_team': 'Gast-Team',
    'tourn.select_team': 'Team wählen…',
    'tourn.kickoff': 'Anpfiff',
    'tourn.new_match': 'Neues Spiel',
    'tourn.live_end': 'Live beenden',
    'tourn.live_start': 'Als Live markieren',
    'tourn.stat_teams': 'Teams',
    'tourn.stat_matches': 'Spiele',
    'tourn.delete_match_confirm': 'Spiel löschen?',

    // --- Schedule (Tagesplan) ---
    'schedule.title': 'Tagesplan',
    'schedule.add_item': '+ Punkt hinzufügen',
    'schedule.no_schedule': 'Noch kein Ablaufplan hinterlegt',
    'schedule.time': 'Uhrzeit',
    'schedule.activity': 'Aktivität',
    'schedule.view': '→ Ablaufplan ansehen',
    'schedule.delete_confirm': 'Eintrag wirklich löschen?',
    'schedule.add_entry': 'Eintrag hinzufügen',
    'schedule.add_day': 'Tag',
    'schedule.no_days': 'Noch keine Tage angelegt',
    'schedule.no_days_desc': 'Füge einen Tag über "+ Tag" hinzu.',
    'schedule.no_entries': 'Keine Einträge für diesen Tag',
    'schedule.no_entries_desc': 'Füge den ersten Programmpunkt hinzu.',
    'schedule.edit_entry': 'Eintrag bearbeiten',
    'schedule.new_entry': 'Neuer Eintrag',
    'schedule.type_entry': 'Programmpunkt',
    'schedule.type_header': 'Überschrift',
    'schedule.type_divider': 'Trenner',
    'schedule.color': 'Farbe',
    'schedule.start_time': 'Startzeit',
    'schedule.duration_min': 'Dauer (Minuten)',
    'schedule.location': 'Ort',
    'schedule.responsible': 'Verantwortlich',
    'schedule.delete_title': 'Eintrag löschen',
    'schedule.delete_msg': 'Soll dieser Eintrag wirklich gelöscht werden?',
    'schedule.delete_day_confirm': 'Diesen Tag mit allen Einträgen löschen?',
    'schedule.days_entries': 'Tage · Einträge',

    // --- Evenings ---
    'evenings.title': 'Camp-Abende',
    'evenings.add': '+ Abend hinzufügen',
    'evenings.no_evenings': 'Keine Abende geplant',
    'evenings.no_evenings_desc': 'Die Abendprogramme werden hier angezeigt.',
    'evenings.edit': 'Abend bearbeiten',
    'evenings.new': 'Neuer Abend',
    'evenings.date': 'Datum',
    'evenings.schedule': 'Ablaufplan',
    'evenings.add_schedule_item': '+ Punkt hinzufügen',
    'evenings.no_schedule': 'Noch kein Ablaufplan hinterlegt',
    'evenings.view_schedule': '→ Ablaufplan ansehen',
    'evenings.delete_confirm': 'Abend wirklich löschen?',
    'evenings.edit_evening': 'Abend bearbeiten',
    'evenings.evening_name': 'Name des Abends',
    'evenings.banner': 'Banner-Bild',
    'evenings.upload_image': 'Bild hochladen',
    'evenings.replace_image': 'Bild ersetzen',
    'evenings.uploading': 'Lädt…',

    // --- Teamtimes ---
    'teamtimes.title': 'Teamtimes',
    'teamtimes.add': '+ Teamtime hinzufügen',
    'teamtimes.no_items': 'Keine Teamtimes vorhanden',
    'teamtimes.edit': 'Teamtime bearbeiten',
    'teamtimes.new': 'Neue Teamtime',
    'teamtimes.topic': 'Thema',
    'teamtimes.team': 'Team',
    'teamtimes.delete_confirm': 'Teamtime wirklich löschen?',
    'teamtimes.field_title': 'Titel',
    'teamtimes.field_date': 'Datum',
    'teamtimes.field_audience': 'Zielgruppe',
    'teamtimes.field_theme': 'Thema',
    'teamtimes.field_bible': 'Bibelstelle',
    'teamtimes.field_content': 'Inhalt',
    'teamtimes.field_questions': 'Gesprächsfragen',
    'teamtimes.field_staff': 'Mitarbeiter-Notizen (intern)',
    'teamtimes.section_content': 'Inhalt',
    'teamtimes.section_questions': 'Gesprächsfragen',
    'teamtimes.section_staff': 'Mitarbeiter-Notizen',
    'teamtimes.attachments': 'Anhänge',
    'teamtimes.no_attachments': 'Keine Anhänge',
    'teamtimes.attach_file': 'Datei anhängen',
    'teamtimes.uploading': 'Lädt…',
    'teamtimes.theme_prefix': 'Thema:',

    // --- Questions ---
    'questions.title': 'Frage des Tages',
    'questions.add': '+ Frage hinzufügen',
    'questions.no_items': 'Keine Fragen vorhanden',
    'questions.edit': 'Frage bearbeiten',
    'questions.new': 'Neue Frage',
    'questions.question': 'Frage',
    'questions.date': 'Datum',
    'questions.delete_confirm': 'Frage wirklich löschen?',

    // --- Staff ---
    'staff.title': 'Mitarbeiter',
    'staff.add': '+ Mitarbeiter hinzufügen',
    'staff.no_staff': 'Keine Mitarbeiter vorhanden',
    'staff.edit': 'Mitarbeiter bearbeiten',
    'staff.new': 'Neuer Mitarbeiter',
    'staff.role': 'Rolle',
    'staff.team': 'Team',
    'staff.delete_confirm': 'Mitarbeiter wirklich entfernen?',
    'staff.delete_title': 'Mitarbeiter löschen',
    'staff.delete_msg': 'Soll dieser Mitarbeiter wirklich gelöscht werden?',
    'staff.name': 'Name',
    'staff.phone': 'Telefon',
    'staff.special_tasks': 'Sonderaufgaben',
    'staff.availability': 'Verfügbarkeit / Notizen',
    'staff.count': '{filtered} von {total} Personen',
    'staff.not_found': 'Keine Mitarbeiter gefunden',
    'staff.no_staff_yet': 'Noch keine Mitarbeiter',
    'staff.no_staff_yet_desc': 'Füge den ersten Mitarbeiter hinzu.',
    'staff.grid_edit': 'Rasteransicht bearbeiten',
    'staff.end_grid_edit': 'Bearbeitung beenden',
    'staff.new_member_ghost': 'Neuer Mitarbeiter',

    // --- ToDos ---
    // --- Checkin ---
    'checkin.title': 'Check-In',
    'checkin.checked_in_of': '{checkedIn} von {total} eingecheckt',
    'checkin.total': 'Gesamt',
    'checkin.checked_in': 'Eingecheckt',
    'checkin.pending': 'Ausstehend',
    'checkin.checked_in_label': '{n} eingecheckt',
    'checkin.search': 'Name suchen…',
    'checkin.no_results': 'Nichts gefunden',
    'checkin.no_results_search': 'Keine Ergebnisse für „{q}"',
    'checkin.no_participants': 'Noch keine Teilnehmer',
    'checkin.undo': 'Rückgängig',
    'checkin.check_in_btn': 'Einchecken',

    // --- Attendance ---
    'attendance.title': 'Anwesenheit',
    'attendance.present_of': '{present} / {total} anwesend',
    'attendance.saved_at': 'Gespeichert {time}',
    'attendance.all_present': 'Alle anwesend',
    'attendance.all_absent': 'Alle abwesend',
    'attendance.present': 'Anwesend',
    'attendance.absent': 'Abwesend',
    'attendance.none': 'Keine',
    'attendance.no_team': 'Ohne Team',
    'attendance.no_participants': 'Keine Teilnehmer gefunden',

    'todos.title': 'ToDos',
    'todos.add': '+ ToDo hinzufügen',
    'todos.no_todos': 'Keine ToDos vorhanden',
    'todos.open': 'Offen',
    'todos.done': 'Erledigt',
    'todos.all': 'Alle',
    'todos.new': 'Neues ToDo',
    'todos.edit': 'ToDo bearbeiten',
    'todos.task': 'Aufgabe',
    'todos.assignee': 'Zuständig',
    'todos.due_date': 'Fälligkeitsdatum',
    'todos.priority': 'Priorität',
    'todos.delete_confirm': 'ToDo wirklich löschen?',
    'todos.open_count': '{count} offen',
    'todos.stat_line': '{open} offen · {done} erledigt',
    'todos.no_open': 'Keine offenen Todos',
    'todos.nothing_done': 'Noch nichts erledigt',
    'todos.title_label': 'Titel',
    'todos.description': 'Beschreibung',
    'todos.due': 'Fällig',
    'todos.assigned_to': 'Zugewiesen an',
    'todos.prio_high': 'Hoch',
    'todos.prio_medium': 'Mittel',
    'todos.prio_low': 'Niedrig',
    'todos.prio_normal': 'Normal',
    'todos.prio_urgent': 'Dringend',

    // --- Kanban Board ---
    'todos.boards': 'Boards',
    'todos.new_board': 'Neues Board',
    'todos.no_boards': 'Noch keine Boards',
    'todos.no_boards_yet': 'Erstelle dein erstes Board, um loszulegen.',
    'todos.create_first_board': 'Erstes Board erstellen',
    'todos.board_name': 'Board-Name',
    'todos.board_desc': 'Beschreibung (optional)',
    'todos.board_settings': 'Board-Einstellungen',
    'todos.add_list': '+ Liste hinzufügen',
    'todos.add_card': '+ Karte hinzufügen',
    'todos.list_name': 'Listenname',
    'todos.card_title': 'Kartentitel',
    'todos.view_kanban': 'Kanban',
    'todos.view_list': 'Tabelle',
    'todos.view_calendar': 'Kalender',
    'todos.view_my_tasks': 'Meine Aufgaben',
    'todos.labels': 'Labels',
    'todos.members': 'Mitglieder',
    'todos.start_date': 'Startdatum',
    'todos.cover_color': 'Coverfarbe',
    'todos.checklist': 'Checkliste',
    'todos.add_checklist': '+ Checkliste hinzufügen',
    'todos.checklist_name': 'Checklistenname',
    'todos.add_item': '+ Element hinzufügen',
    'todos.checklist_progress': '{done} / {total} erledigt',
    'todos.comments': 'Kommentare',
    'todos.write_comment': 'Kommentar schreiben...',
    'todos.activity': 'Aktivität',
    'todos.move_to_list': 'In Liste verschieben',
    'todos.archive_card': 'Karte archivieren',
    'todos.archive_board': 'Board archivieren',
    'todos.templates': 'Vorlagen',
    'todos.save_as_template': 'Als Vorlage speichern',
    'todos.load_template': 'Vorlage laden',
    'todos.filter_assignee': 'Mitglied',
    'todos.filter_priority': 'Priorität',
    'todos.filter_label': 'Label',
    'todos.filter_due': 'Fälligkeit',
    'todos.my_tasks': 'Meine Aufgaben',
    'todos.overdue': 'Überfällig',
    'todos.today': 'Heute',
    'todos.upcoming': 'Demnächst',
    'todos.no_date': 'Kein Datum',
    'todos.today_section': 'Heute fällig',
    'todos.overdue_section': 'Überfällig',
    'todos.upcoming_section': 'Demnächst',
    'todos.no_cards': 'Keine Karten',
    'todos.no_my_tasks': 'Dir sind keine Aufgaben zugewiesen.',
    'todos.card_archived': 'Karte archiviert',
    'todos.board_archived': 'Board archiviert',
    'todos.list_archived': 'Liste archiviert',
    'todos.duplicate_board': 'Board duplizieren',
    'todos.rename_list': 'Liste umbenennen',
    'todos.delete_list': 'Liste archivieren',
    'todos.mark_done_list': 'Als Erledigt-Liste markieren',
    'todos.activity_created': 'hat diese Karte erstellt',
    'todos.activity_moved': 'hat diese Karte verschoben',
    'todos.activity_commented': 'hat kommentiert',
    'todos.activity_archived': 'hat diese Karte archiviert',
    'todos.activity_assigned': 'hat Mitglieder geändert',
    'todos.activity_due_date_changed': 'hat das Fälligkeitsdatum geändert',
    'todos.activity_label_changed': 'hat Labels geändert',
    'todos.no_description': 'Keine Beschreibung hinzugefügt.',
    'todos.edit_description': 'Beschreibung bearbeiten...',
    'todos.cover_none': 'Kein Cover',
    'todos.export_csv': 'Als CSV exportieren',
    'todos.export_xlsx': 'Als Excel exportieren',
    'todos.template_name': 'Vorlagenname',
    'todos.apply_template': 'Vorlage anwenden',
    'todos.done_list_badge': 'Erledigt-Liste',
    'todos.list_has_cards': 'Diese Liste enthält noch aktive Karten.',

    // --- Lists ---
    'lists.title': 'Listen',
    'lists.add': '+ Liste hinzufügen',
    'lists.no_lists': 'Keine Listen vorhanden',
    'lists.no_lists_hint': 'Noch keine Listen.\nKlicke auf „Neue Liste".',
    'lists.edit': 'Liste bearbeiten',
    'lists.new': 'Neue Liste',
    'lists.new_short': 'Neu',
    'lists.delete_confirm': 'Liste wirklich löschen?',
    'lists.delete_list_title': 'Liste löschen',
    'lists.delete_list_msg': 'Diese Liste und alle Zeilen werden dauerhaft gelöscht.',
    'lists.delete_row_title': 'Zeile löschen',
    'lists.delete_row_msg': 'Diese Zeile wird dauerhaft gelöscht.',
    'lists.add_item': '+ Eintrag',
    'lists.items': 'Einträge',
    'lists.entries': 'Einträge',
    'lists.of': 'von',
    'lists.select_list': 'Liste auswählen',
    'lists.select_list_desc': 'Wähle links eine Liste oder erstelle eine neue.',
    'lists.select_mobile': 'Liste wählen',
    'lists.add_row': 'Hinzufügen',
    'lists.new_row': 'Neue Zeile',
    'lists.edit_row': 'Zeile bearbeiten',
    'lists.columns_btn': 'Spalten',
    'lists.manage_columns': 'Spalten verwalten',
    'lists.no_columns': 'Noch keine Spalten definiert',
    'lists.no_columns_desc': 'Klicke auf „Spalten", um Spalten hinzuzufügen.',
    'lists.grid_done': 'Fertig',
    'lists.grid_mode': 'Raster',
    'lists.selected_n': '{n} ausgewählt',
    'lists.empty_rows': 'Keine Einträge — klicke auf „+ Hinzufügen"',
    'lists.no_results_search': 'Keine Ergebnisse',
    'lists.template': 'Vorlage verwenden',
    'lists.template_empty': 'Leer (kein Template)',
    'lists.row_no_columns': 'Noch keine Spalten definiert.',
    'lists.no_options': 'Keine Optionen definiert — bitte in Spalten-Einstellungen hinzufügen.',
    'lists.person_placeholder': 'Person suchen…',
    'lists.no_selection': '— keine Auswahl —',
    'lists.icon': 'Icon',

    // --- Files ---
    'files.title': 'Dateien',
    'files.upload': '+ Datei hochladen',
    'files.no_files': 'Keine Dateien vorhanden',
    'files.delete_confirm': 'Datei wirklich löschen?',
    'files.folder': 'Ordner',
    'files.new_folder': 'Neuer Ordner',
    'files.file_name': 'Dateiname',
    'files.size': 'Größe',
    'files.uploaded_at': 'Hochgeladen am',
    'files.file': 'Datei',
    'files.files': 'Dateien',
    'files.upload_btn': 'Hochladen',
    'files.upload_error': 'Upload fehlgeschlagen. Bitte erneut versuchen.',
    'files.drop_hint': 'Dateien hierher ziehen zum Hochladen · max. 50 MB pro Datei',
    'files.select_btn': 'Auswählen',
    'files.no_results': 'Keine Ergebnisse',
    'files.no_results_for': 'Keine Treffer für',
    'files.empty_desc': 'Ziehe Dateien hierher oder klicke auf „Hochladen"',
    'files.rename_folder': 'Ordner umbenennen',
    'files.new_name': 'Neuer Name',
    'files.delete_file_title': 'Datei löschen',
    'files.delete_file_msg': 'Diese Datei wird dauerhaft gelöscht.',
    'files.delete_folder_title': 'Ordner löschen',
    'files.delete_folder_msg': 'Dieser Ordner und alle Inhalte werden dauerhaft gelöscht.',
    'files.items_count': 'Elemente',
    'files.items_short': 'Elem.',
    'files.no_entries': 'Keine Einträge gefunden',
    'files.selected': 'ausgewählt',
    'files.list_view': 'Listenansicht',
    'files.grid_view': 'Rasteransicht',
    'files.col_type': 'Typ',
    'files.folder_type': 'Ordner',

    // --- Meals ---
    'meals.title': 'Küche & Mahlzeiten',
    'meals.add': '+ Mahlzeit',
    'meals.no_meals': 'Noch keine Mahlzeiten',
    'meals.edit': 'Mahlzeit bearbeiten',
    'meals.new': 'Neue Mahlzeit',
    'meals.meal': 'Mahlzeit',
    'meals.date': 'Datum',
    'meals.portions': 'Portionen',
    'meals.delete_confirm': 'Mahlzeit löschen?',
    'meals.shopping_list': 'Einkaufsliste',
    'meals.no_date': 'Kein Datum',
    'meals.field_title': 'Titel',
    'meals.field_type': 'Typ',
    'meals.notes_ingredients': 'Notizen / Zutaten',
    'meals.type.breakfast': 'Frühstück',
    'meals.type.lunch': 'Mittagessen',
    'meals.type.dinner': 'Abendessen',
    'meals.type.snack': 'Snack',

    // --- Finance ---
    'finance.title': 'Finanzen',
    'finance.add': '+ Eintrag hinzufügen',
    'finance.no_items': 'Keine Einträge vorhanden',
    'finance.edit': 'Eintrag bearbeiten',
    'finance.new': 'Neuer Eintrag',
    'finance.income': 'Einnahmen',
    'finance.expense': 'Ausgaben',
    'finance.balance': 'Bilanz',
    'finance.amount': 'Betrag',
    'finance.category': 'Kategorie',
    'finance.description': 'Beschreibung',
    'finance.date': 'Datum',
    'finance.delete_confirm': 'Eintrag wirklich löschen?',

    // --- Equipment ---
    'equipment.title': 'Equipment',
    'equipment.add': '+ Equipment hinzufügen',
    'equipment.no_items': 'Kein Equipment vorhanden',
    'equipment.edit': 'Equipment bearbeiten',
    'equipment.new': 'Neues Equipment',
    'equipment.item': 'Gegenstand',
    'equipment.quantity': 'Menge',
    'equipment.condition': 'Zustand',
    'equipment.location': 'Lagerort',
    'equipment.delete_confirm': 'Equipment wirklich löschen?',
    'equipment.col_category': 'Kategorie',
    'equipment.col_notes': 'Notizen',
    'equipment.cond.new': 'Neu',
    'equipment.cond.good': 'Gut',
    'equipment.cond.fair': 'Mittel',
    'equipment.cond.poor': 'Schlecht',

    // --- Downloads / Flyer ---
    'downloads.title': 'Downloads',
    'downloads.add': '+ Datei hinzufügen',
    'downloads.no_items': 'Keine Downloads vorhanden',
    'downloads.edit': 'Eintrag bearbeiten',
    'downloads.new': 'Neuer Eintrag',
    'downloads.delete_confirm': 'Download wirklich löschen?',
    'downloads.file': 'Datei',
    'downloads.category': 'Kategorie',
    'downloads.subtitle': 'Dateien für die öffentliche Downloads-Seite',
    'downloads.add_file': 'Datei hinzufügen',
    'downloads.no_items_desc': 'Füge Flyer, Formulare und andere Materialien hinzu.',
    'downloads.file_available': 'Datei vorhanden',
    'downloads.public': 'Öffentlich',
    'downloads.hidden': 'Versteckt',
    'downloads.visibility': 'Sichtbarkeit',
    'downloads.public_visible': 'Öffentlich sichtbar',
    'downloads.select_file': 'Datei auswählen',
    'downloads.edit_file': 'Datei bearbeiten',
    'downloads.new_file': 'Neue Datei',
    'downloads.toggle_show': 'Öffentlich – klicken zum Verstecken',
    'downloads.toggle_hide': 'Versteckt – klicken zum Veröffentlichen',
    'downloads.delete_file_confirm': 'Datei löschen?',

    // --- Statistics ---
    'stats.title': 'Statistiken',
    'stats.subtitle': 'Überblick über das Camp',
    'stats.participants': 'Teilnehmer',
    'stats.checked_in': 'Eingecheckt',
    'stats.teams': 'Teams',
    'stats.paid': 'Bezahlt',
    'stats.today_present': 'Heute anwesend',
    'stats.open_todos': 'Offene Todos',
    'stats.attendance_rate': 'Anwesenheitsquote',
    'stats.payment_rate': 'Zahlungsquote',
    'stats.progress': 'Fortschritt',
    'stats.att_week': 'Anwesenheit – letzte 7 Tage',
    'stats.team_sizes': 'Team-Größen',
    'questions.answer_label': 'Antwort',
    'questions.answer_placeholder': 'Antwort eingeben...',
    'questions.answer_btn': 'Antworten',
    'questions.no_questions': 'Noch keine Fragen',

    // --- Dashboard ---
    'dash.to_camp': 'Zur Camp-Seite',
    'dash.settings': 'Einstellungen',

    // --- Forms / Registration ---
    'forms.title': 'Formulare',
    'forms.registration': 'Anmeldeformular',
    'forms.add': '+ Formular hinzufügen',
    'forms.no_forms': 'Keine Formulare vorhanden',
    'forms.edit': 'Formular bearbeiten',
    'forms.new': 'Neues Formular',
    'forms.field': 'Feld',
    'forms.label': 'Bezeichnung',
    'forms.required': 'Pflichtfeld',
    'forms.delete_confirm': 'Formular wirklich löschen?',
    'forms.responses': 'Antworten',
    'forms.no_responses': 'Keine Antworten vorhanden',
    'forms.subtitle': 'Erstelle und verwalte Anmeldeformulare für dein Camp',
    'forms.no_forms_title': 'Noch kein Formular',
    'forms.no_forms_desc': 'Erstelle dein erstes Anmeldeformular.',
    'forms.create': 'Formular erstellen',
    'forms.active': 'Aktiv',
    'forms.inactive': 'Inaktiv',
    'forms.fields_count': '{n} Felder',
    'forms.submissions_count': '{n} Anmeldungen',
    'forms.submissions': 'Anmeldungen',
    'forms.delete_form_confirm': 'Formular und alle Felder löschen?',
    'forms.load_error': 'Formular konnte nicht geladen werden.',
    'forms.create_error': 'Fehler beim Erstellen.',
    'forms.saved': 'Gespeichert ✓',
    'forms.save_error': 'Fehler!',
    'forms.unsaved_confirm': 'Ungespeicherte Änderungen verwerfen?',
    'forms.back': 'Zurück',
    'forms.back_to_builder': 'Zurück zum Builder',
    'forms.preview_label': 'Vorschau',
    'forms.saving': 'Speichern…',
    'forms.save_dirty': 'Speichern*',
    'forms.saved_btn': 'Gespeichert',
    'forms.description_label': 'Beschreibung',
    'forms.description_placeholder': 'Kurze Beschreibung des Formulars (öffentlich sichtbar)',
    'forms.confirmation_label': 'Bestätigungstext',
    'forms.confirmation_placeholder': 'Text nach erfolgreicher Anmeldung...',
    'forms.button_color': 'Button-Farbe',
    'forms.add_fields': 'Felder hinzufügen',
    'forms.add_field_btn': '+ Feld hinzufügen',
    'forms.field_count': '{n} Felder',
    'forms.canvas_empty': 'Füge links Felder hinzu, um dein Formular zu gestalten.',
    'forms.select_field': 'Feld auswählen zum Bearbeiten',
    'forms.activate_title': 'Formular aktivieren (öffentlich zugänglich machen)',
    'forms.drag_hint': 'Ziehen zum Neuanordnen',
    'forms.move_up': 'Nach oben',
    'forms.move_down': 'Nach unten',
    'forms.name_label': 'Name des Formulars *',
    'forms.name_placeholder': 'z.B. Anmeldung Baseball Camp 2026',
    'forms.submit_preview': 'Anmelden → (Vorschau)',
    'forms.select_placeholder': 'Bitte wählen…',
    'forms.submissions_title': 'Anmeldungen: {name}',
    'forms.submissions_count_msg': '{n} Anmeldungen eingegangen',
    'forms.no_submissions': 'Noch keine Anmeldungen',
    'forms.no_submissions_desc': 'Sobald jemand das Formular ausfüllt, erscheint er hier.',
    'forms.col_name': 'Name',
    'forms.col_email': 'E-Mail',
    'forms.col_status': 'Status',
    'forms.col_registered': 'Angemeldet am',
    'forms.fe_edit_section': 'Abschnitt bearbeiten',
    'forms.fe_section_title': 'Titel',
    'forms.fe_section_desc': 'Beschreibung (optional)',
    'forms.fe_section_desc_placeholder': 'Kurze Erklärung für diesen Abschnitt',
    'forms.fe_delete_section': 'Abschnitt löschen',
    'forms.fe_edit_field': 'Feld bearbeiten',
    'forms.fe_field_type': 'Feldtyp',
    'forms.fe_label_de': 'Label (DE) *',
    'forms.fe_label_en': 'Label (EN, optional)',
    'forms.fe_help_text': 'Hilfetext',
    'forms.fe_help_placeholder': 'Wird unter dem Feld angezeigt...',
    'forms.fe_placeholder': 'Platzhalter',
    'forms.fe_required': 'Pflichtfeld',
    'forms.fe_mapping': 'Mapping → Teilnehmerfeld',
    'forms.fe_mapping_desc': 'Weist die Antwort automatisch dem Teilnehmerprofil zu.',
    'forms.fe_options': 'Optionen',
    'forms.fe_option_label': 'Bezeichnung',
    'forms.fe_option_value': 'Wert',
    'forms.fe_add_option': '+ Option',
    'forms.fe_delete_field': 'Feld löschen',
    'forms.ft_text': 'Textfeld',
    'forms.ft_textarea': 'Mehrzeiliger Text',
    'forms.ft_email': 'E-Mail',
    'forms.ft_phone': 'Telefonnummer',
    'forms.ft_date': 'Datum',
    'forms.ft_number': 'Zahlenfeld',
    'forms.ft_select': 'Auswahlfeld',
    'forms.ft_multiselect': 'Mehrfachauswahl',
    'forms.ft_checkbox': 'Checkbox',
    'forms.ft_yesno': 'Ja / Nein',

    // --- Camp Admin Settings ---
    'admin.title': 'Camp-Einstellungen',
    'admin.modules': 'Module',
    'admin.modules_desc': 'Aktiviere oder deaktiviere Funktionen für dieses Camp.',
    'admin.branding': 'Design & Branding',
    'admin.general': 'Allgemein',
    'admin.users': 'Benutzer',
    'admin.users_add': '+ Benutzer erstellen',
    'admin.users_edit': 'Benutzer bearbeiten',
    'admin.users_new': 'Neuer Benutzer',
    'admin.users_search': 'Benutzer suchen...',
    'admin.username': 'Benutzername',
    'admin.display_name': 'Anzeigename',
    'admin.email': 'E-Mail',
    'admin.role': 'Rolle',
    'admin.roles': 'Rollen',
    'admin.assign_teams': 'Teams zuweisen',
    'admin.account_active': 'Konto aktiv',
    'admin.active': 'Aktiv',
    'admin.inactive': 'Inaktiv',
    'admin.delete_user_confirm': 'Benutzer wirklich löschen?',
    'admin.permissions': 'Berechtigungen',
    'admin.simulate': 'Als dieser Nutzer simulieren',
    'admin.tab_general': 'Allgemein',
    'admin.tab_camps': 'Camps',
    'admin.tab_users': 'Benutzer',
    'admin.tab_permissions': 'Rollen & Rechte',
    'admin.tab_branding': 'Branding & Farben',
    'admin.tab_cms': 'Landing Page',
    'admin.tab_modules': 'Module',
    'admin.general_title': 'Camp-Info',
    'admin.general_readonly': 'Camp-Stammdaten können nur von Superadmins bearbeitet werden.',
    'admin.field_location': 'Ort',
    'admin.field_start': 'Start',
    'admin.field_end': 'Ende',
    'admin.camps_manage': 'Verwalte die jährlichen Instanzen dieses Camps',
    'admin.camps_org': 'Organisation: {name}',
    'admin.new_camp': 'Neues Camp',
    'admin.no_camps': 'Noch keine weiteren Camps in diesem Tenant',
    'admin.camp_current': 'Aktuell',
    'admin.camp_active': 'Aktiv',
    'admin.camp_activate': 'Aktivieren',
    'admin.camp_delete_title': 'Camp löschen',
    'admin.camp_delete_msg': 'Dieses Camp und alle zugehörigen Teilnehmer werden dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
    'admin.slug_hint': '(URL-Kennung)',
    'admin.field_year': 'Jahr',
    'admin.creating': 'Erstellen…',
    'admin.create_camp': 'Camp erstellen',
    'admin.users_section_title': 'Camp-Benutzer',
    'admin.users_desc': 'Verwalte Zugänge und Rollen für dieses Camp',
    'admin.no_users': 'Noch keine Benutzer in diesem Camp',
    'admin.password': 'Passwort',
    'admin.remove_user_title': 'Benutzer entfernen',
    'admin.remove_user_msg': 'Soll dieser Benutzer aus dem Camp entfernt werden? Der Account bleibt erhalten.',
    'admin.remove': 'Entfernen',
    'admin.roles_label': 'Rollen',
    'admin.new_role_title': 'Neue Rolle erstellen',
    'admin.role_display_name': 'Anzeigename',
    'admin.role_key': 'Rollenkennung',
    'admin.role_key_hint': '(nur a-z, 0-9, _)',
    'admin.create_role': 'Rolle erstellen',
    'admin.select_role_hint': 'Wähle eine Rolle aus, um die Rechte zu bearbeiten.',
    'admin.role_delete_confirm': 'Rolle "{name}" löschen?',
    'admin.branding_title': 'Logo & Theme',
    'admin.branding_desc': 'Passe Logo und Farben der Camp-Umgebung an eure CI an. Die Änderungen wirken sich auf die interne App, die öffentliche Seite und alle Camp-Bereiche aus.',
    'admin.logo_section': 'Camp-Logo',
    'admin.logo_hint': 'Wird im Header und als PWA-Icon verwendet. Empfohlen: quadratisch, min. 512×512px.',
    'admin.logo_upload': 'Logo hochladen',
    'admin.logo_change': 'Logo ändern',
    'admin.logo_remove': 'Entfernen',
    'admin.logo_uploading': 'Wird hochgeladen…',
    'admin.live_preview': 'Live Vorschau',
    'admin.primary_color': 'Primärfarbe',
    'admin.primary_hint': 'Buttons, aktive Navigation, Akzente – die Hauptfarbe eures Camps.',
    'admin.secondary_color': 'Sekundärfarbe',
    'admin.secondary_hint': 'Zweite Akzentfarbe für Highlights und Badges.',
    'admin.accent_color': 'Akzent',
    'admin.accent_hint': 'Dritte Farbe für Tabs und spezielle Markierungen.',
    'admin.save_branding': 'Theme speichern',
    'admin.saved_branding': 'Theme gespeichert!',
    'admin.cms_title': 'Öffentliche Seite',
    'admin.hero_tagline': 'Hero-Tagline',
    'admin.hero_subtitle': 'Hero-Untertitel',
    'admin.camp_type': 'Camp-Typ',
    'admin.age_group': 'Altersgruppe',
    'admin.price_info': 'Preis-Info',
    'admin.save_cms': 'Landing Page speichern',
    'admin.saved_cms': 'Landing Page gespeichert!',
    'admin.modules_title': 'Module',
    'admin.save_modules': 'Module speichern',
    'admin.saved_modules': 'Module gespeichert!',
    'admin.saved_role': 'Rolle gespeichert!',
    'admin.error_activate': 'Fehler beim Aktivieren',
    'admin.error_create': 'Fehler beim Erstellen',
    'admin.error_simulate': 'Fehler beim Simulieren',
    'admin.color_reset': 'Zurücksetzen',
    'admin.adding': 'Hinzufügen',
    'admin.tab_push': 'Push',
    'admin.tab_registrations': 'Registrierungen',
    'admin.reg_empty': 'Keine Registrierungsanfragen',
    'admin.reg_approve': 'Freischalten',
    'admin.reg_reject': 'Ablehnen',
    'admin.reg_approved': 'Freigeschaltet',
    'admin.reg_rejected': 'Abgelehnt',
    'admin.reg_pending': 'Ausstehend',
    'admin.reg_message': 'Nachricht',
    'admin.reg_requested_at': 'Angefragt am',
    'admin.push_subscribers': 'Abonnenten',
    'admin.push_send_title': 'Benachrichtigung senden',
    'admin.push_title_label': 'Titel',
    'admin.push_title_placeholder': 'z.B. Neue Ankündigung vom Camp',
    'admin.push_body_label': 'Text',
    'admin.push_body_placeholder': 'Nachricht die die Nutzer erhalten...',
    'admin.push_url_label': 'Link (optional)',
    'admin.push_url_hint': 'Wohin soll ein Klick auf die Benachrichtigung führen?',
    'admin.push_send_btn': 'Senden an',
    'admin.push_sending': 'Wird gesendet...',
    'admin.push_sent': '{sent} von {total} erfolgreich gesendet',
    'admin.push_no_subscribers': 'Keine Abonnenten — erst auf der News-Seite aktivieren',
    'admin.push_auto_title': 'Automatische Artikel-Benachrichtigung',
    'admin.push_auto_desc': 'Wird automatisch gesendet wenn ein Artikel veröffentlicht wird.',
    'admin.push_auto_enabled': 'Automatisch senden',
    'admin.push_auto_title_tpl': 'Titel-Vorlage',
    'admin.push_auto_body_tpl': 'Text-Vorlage',
    'admin.push_auto_vars': 'Verfügbare Variablen: {title}, {teaser}',
    'admin.push_auto_save': 'Vorlage speichern',
    'admin.push_auto_saved': 'Gespeichert',
    'admin.session_title': 'Altersgruppen-Zuteilung',
    'admin.session_desc': 'Legt fest, ab welchem Alter Teilnehmer automatisch Vormittag oder Nachmittag zugewiesen werden.',
    'admin.morning_max_age': 'Vormittag – Maximalalter',
    'admin.morning_max_age_desc': 'Teilnehmer bis zu diesem Alter werden automatisch Vormittag zugewiesen.',
    'admin.afternoon_min_age': 'Nachmittag – Mindestalter',
    'admin.afternoon_min_age_desc': 'Teilnehmer ab diesem Alter werden automatisch Nachmittag zugewiesen.',
    'admin.session_auto_note': 'Die Zuteilung erfolgt automatisch beim Anlegen eines Teilnehmers und kann danach manuell angepasst werden.',

    // --- SuperAdmin ---
    'sa.dashboard': 'Dashboard',
    'sa.tenants': 'Tenants',
    'sa.camps': 'Camps',
    'sa.users': 'Benutzer',
    'sa.equipment': 'Equipment',
    'sa.templates': 'Vorlagen',
    'sa.backups': 'Backups',
    'sa.settings': 'Einstellungen',
    'sa.logout': 'Abmelden',
    'sa.to_platform': 'Zur Plattform',
    'sa.superadmin': 'SuperAdmin',
    'sa.platform': 'Baseball Camp Platform',
    'sa.create_backup': 'Backup erstellen',
    'sa.restore': 'Wiederherstellen',
    'sa.delete_confirm': 'Wirklich löschen?',

    // --- Platform landing ---
    'platform.title': 'Baseball Camp Platform',
    'platform.subtitle': 'Verwaltung für dein Camp',
    'platform.admin_login': 'Admin Login',
    'platform.camp_login': 'Camp Login',
    'platform.enter_slug': 'Camp-Slug eingeben...',
    'platform.go': 'Los',

    // --- Push notifications ---
    'push.active': 'Push-Benachrichtigungen aktiv',
    'push.disable': 'Deaktivieren',
    'push.success': 'Benachrichtigungen aktiviert!',
    'push.blocked': 'Benachrichtigungen blockiert — in Browser-Einstellungen aktivieren',
    'push.enable_prompt': 'Benachrichtigungen aktivieren?',
    'push.title': 'Benachrichtigungen aktivieren?',
    'push.desc': 'Werde sofort benachrichtigt, wenn es neue News vom Camp gibt.',
    'push.enable': 'Aktivieren',
    'push.not_now': 'Nicht jetzt',
  },

  // =========================================================================
  // ENGLISH
  // =========================================================================
  en: {
    // --- Languages ---
    'lang.de': 'Deutsch',
    'lang.en': 'English',
    'lang.es': 'Español',

    // --- Common actions ---
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.add': 'Add',
    'common.close': 'Close',
    'common.back': '← Back',
    'common.loading': 'Loading...',
    'common.saving': 'Saving...',
    'common.deleting': 'Deleting...',
    'common.search': 'Search...',
    'common.filter': 'Filter',
    'common.reset': 'Reset',
    'common.columns': 'Columns',
    'common.confirm': 'Confirm',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.apply': 'Apply',
    'common.export': 'Export',
    'common.import': 'Import',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.rename': 'Rename',
    'common.duplicate': 'Duplicate',
    'common.preview': 'Preview',
    'common.publish': 'Publish',
    'common.archive': 'Archive',
    'common.activate': 'Activate',
    'common.deactivate': 'Deactivate',
    'common.enable': 'Enable',
    'common.disable': 'Disable',
    'common.optional': 'optional',
    'common.required': 'Required',
    'common.all': 'All',
    'common.none': 'None',
    'common.or': 'or',
    'common.and': 'and',
    'common.by': 'by',
    'common.at': 'at',
    'common.on': 'on',
    'common.from': 'from',
    'common.to': 'to',
    'common.of': 'of',
    'common.items': 'items',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.type': 'Type',
    'common.name': 'Name',
    'common.title': 'Title',
    'common.description': 'Description',
    'common.notes': 'Notes',
    'common.date': 'Date',
    'common.time': 'Time',
    'common.email': 'Email',
    'common.phone': 'Phone',
    'common.address': 'Address',
    'common.image': 'Image',
    'common.images': 'Images',
    'common.file': 'File',
    'common.files': 'Files',
    'common.size': 'Size',
    'common.color': 'Color',
    'common.number': '#',
    'common.total': 'Total',
    'common.count': 'Count',
    'common.yes_delete': 'Yes, delete',
    'common.confirm_delete_title': 'Are you sure?',
    'common.confirm_delete': 'Really delete?',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.warning': 'Warning',
    'common.info': 'Info',
    'common.no_access': 'No Access',
    'common.no_access_desc': 'You do not have permission to view this page.',
    'common.not_found': 'Not found',
    'common.to_home': 'To Home',
    'common.copy': 'Copy',
    'common.copied': 'Copied!',
    'common.share': 'Share',
    'common.link': 'Link',
    'common.read_more': 'Read more',
    'common.show_less': 'Show less',

    // --- Roles ---
    'role.admin': 'Admin',
    'role.coach': 'Coach',
    'role.social_media': 'Social Media',
    'role.staff': 'Staff',
    'role.viewer': 'Viewer',
    'role.show_master': 'Show Master',
    'role.tournament_manager': 'Tournament Manager',
    'role.superadmin': 'Superadmin',

    // --- Module names ---
    'module.news': 'News',
    'module.teams': 'Teams',
    'module.tournament': 'Tournament',
    'module.registration': 'Registration',
    'module.flyer': 'Downloads',
    'module.finance': 'Finance',
    'module.teamtimes': 'Team Times',
    'module.lists': 'Lists',
    'module.files': 'Files',
    'module.todos': 'To-Dos',
    'module.meals': 'Kitchen',
    'module.staff': 'Staff',
    'module.equipment': 'Equipment',
    'module.checkin': 'Check-In',
    'module.schedule': 'Schedule',
    'module.evenings': 'Evenings',
    'module.questions': 'Question of the Day',
    'module.statistics': 'Statistics',
    'module.forms': 'Forms',
    'module.settings': 'Settings',

    // --- Sidebar nav ---
    'nav.dashboard': 'Dashboard',
    'nav.section.camp': 'CAMP',
    'nav.section.program': 'PROGRAM',
    'nav.section.org': 'ORGANIZATION',
    'nav.section.admin': 'ADMIN',
    'nav.news': 'News',
    'nav.participants': 'Participants',
    'nav.teams': 'Teams',
    'nav.attendance': 'Attendance',
    'nav.checkin': 'Check-In',
    'nav.tournament': 'Tournament',
    'nav.schedule': 'Schedule',
    'nav.evenings': 'Evenings',
    'nav.teamtimes': 'Team Times',
    'nav.questions': "Q of the Day",
    'nav.staff': 'Staff',
    'nav.todos': 'To-Dos',
    'nav.lists': 'Lists',
    'nav.files': 'Files',
    'nav.meals': 'Kitchen',
    'nav.finance': 'Finance',
    'nav.equipment': 'Equipment',
    'nav.downloads': 'Downloads',
    'nav.statistics': 'Statistics',
    'nav.forms': 'Forms',
    'nav.emails': 'Email',
    'nav.camp_settings': 'Camp Settings',
    'nav.to_public': 'Public Site',
    'nav.logout': 'Sign Out',
    'nav.expand': 'Expand',
    'nav.collapse': 'Collapse',
    'nav.public_label': 'Public',
    'nav.intern_area': 'Staff Area',

    // --- Layout / App shell strings ---
    'layout.active': 'Active',
    'layout.archive': 'Archive',
    'layout.archive_banner': 'Archive view — {name} is not the active camp. No data will be changed.',
    'layout.to_active_camp': 'Go to active camp →',
    'layout.simulation': 'Simulation',
    'layout.simulation_banner': 'Simulation: {name}',
    'layout.end_simulation': 'End simulation',
    'layout.camp_not_found': 'Camp not found',
    'layout.loading': 'Loading...',
    'layout.menu': 'Menu',
    'layout.to_platform': 'To Platform',
    'layout.to_camp': 'Camp page',
    'layout.login': 'Sign in',
    'layout.intern': 'Staff',
    'layout.datenschutz': 'Privacy',

    // --- Public nav ---
    'pub.home': 'Home',
    'pub.news': 'News',
    'pub.teams': 'Teams',
    'pub.tournament': 'Tournament',
    'pub.evenings': 'Evenings',
    'pub.registration': 'Registration',
    'pub.downloads': 'Downloads',

    // --- Auth ---
    'auth.login_title': 'Sign In',
    'auth.username_or_email': 'Username or Email',
    'auth.password': 'Password',
    'auth.login_btn': 'Sign In',
    'auth.logging_in': 'Signing in...',
    'auth.login_failed': 'Login failed',
    'auth.logout': 'Sign Out',
    'auth.camp_login': 'Camp Login',
    'auth.back_to_site': 'Back to camp site',
    'auth.password_hint': 'Password (leave empty = unchanged)',
    'auth.register_link': "Don't have an account? Register now",
    'auth.register_title': 'Request Access',
    'auth.register_subtitle': 'Your account will need to be approved by camp management.',
    'auth.display_name': 'Name / Display name',
    'auth.register_message': 'Message to camp management (optional)',
    'auth.register_btn': 'Send request',
    'auth.registering': 'Sending...',
    'auth.register_success_title': 'Request submitted',
    'auth.register_success_body': 'Camp management will review and activate your account.',
    'auth.back_to_login': 'Back to login',
    'auth.pending_approval': 'Your account still needs to be activated by camp management.',
    'profile.title': 'My Profile',
    'profile.account_info': 'Account Information',
    'profile.display_name': 'Display Name',
    'profile.username': 'Username',
    'profile.email': 'Email',
    'profile.role': 'Role',
    'profile.member_since': 'Member since',
    'profile.save': 'Save changes',
    'profile.saving': 'Saving...',
    'profile.saved': 'Saved',
    'profile.change_password': 'Change Password',
    'profile.current_password': 'Current Password',
    'profile.new_password': 'New Password',
    'profile.confirm_password': 'Confirm Password',
    'profile.password_mismatch': 'Passwords do not match',
    'profile.password_min': 'At least 8 characters',
    'profile.update_password': 'Update Password',
    'profile.updating': 'Updating...',
    'profile.updated': 'Password updated',

    // --- Dashboard ---
    'dashboard.to_camp_site': 'To Camp Site',
    'dashboard.participants': 'Participants',
    'dashboard.checked_in': 'Checked In',
    'dashboard.teams': 'Teams',
    'dashboard.paid': 'Paid',
    'dashboard.att_today': 'Present Today',
    'dashboard.open_todos': 'Open Todos',
    'dashboard.settings': 'Settings',

    // --- News ---
    'news.title': 'News',
    'news.subtitle': 'Current news, reports and announcements about the camp.',
    'news.create': '+ Create Post',
    'news.edit': 'Edit Post',
    'news.new': 'New Post',
    'news.no_news': 'No news yet',
    'news.no_news_desc': 'Check back later.',
    'news.read_more': 'Read more',
    'news.read': 'Read',
    'news.pinned': 'PINNED',
    'news.featured': 'FEATURED',
    'news.by': 'by',
    'news.images': 'Images',
    'news.more_news': 'More News',
    'news.back': 'Back to News',
    'news.not_found': 'Article not found',
    'news.not_found_desc': 'This post does not exist or has been archived.',
    'news.share': 'Share:',
    'news.copy_link': 'Copy link',
    'news.copied': 'Copied!',
    'news.article_count': '{count} posts total',
    'news.filter_all': 'All',
    'news.pin': 'Pin',
    'news.unpin': 'Unpin',
    'news.set_featured': 'Set as featured',
    'news.set_homepage': 'Show on homepage',
    'news.slug_label': 'URL Slug',
    'news.slug_hint': 'Generated automatically',
    'news.teaser': 'Teaser / Preview text',
    'news.content': 'Content',
    'news.hero_image': 'Hero image',
    'news.gallery': 'Image gallery',
    'news.gallery_hint': 'Drag & drop to reorder images',
    'news.seo_title': 'SEO Title',
    'news.seo_description': 'SEO Description',
    'news.tags': 'Tags',
    'news.publish_at': 'Publish at',
    'news.expires_at': 'Expiry date',
    'news.duplicate': 'Duplicate',
    'news.status_change': 'Change status',
    'news.delete_confirm': 'Really delete this post?',
    'news.lang_tab_hint': 'Switch the language of text fields. Images and metadata stay the same across languages.',

    // News status
    'news.status.draft': 'Draft',
    'news.status.scheduled': 'Scheduled',
    'news.status.published': 'Published',
    'news.status.archived': 'Archived',

    // News categories
    'news.cat.general': 'General',
    'news.cat.registration': 'Registration',
    'news.cat.tournament': 'Tournament',
    'news.cat.program': 'Program',
    'news.cat.teams': 'Teams',
    'news.cat.staff': 'Staff',
    'news.cat.parents': 'Parents',
    'news.cat.reminder': 'Reminder',
    'news.cat.recap': 'Recap',
    'news.cat.important': 'Important',

    // News admin list
    'news.admin.title': 'Manage News',
    'news.admin.all': 'All',
    'news.admin.drafts': 'Drafts',
    'news.admin.published': 'Published',
    'news.admin.scheduled': 'Scheduled',
    'news.admin.archived': 'Archived',
    'news.admin.no_results': 'No posts found',
    'news.admin.search': 'Search posts...',

    // --- Participants ---
    'participants.title': 'Participants',
    'participants.add': '+ Participant',
    'participants.search': 'Search participants...',
    'participants.import_csv': '📤 CSV Import',
    'participants.first_name': 'First Name',
    'participants.last_name': 'Last Name',
    'participants.jersey': 'Jersey Number',
    'participants.age': 'Age',
    'participants.team': 'Team',
    'participants.all_teams': 'All Teams',
    'participants.position': 'Position',
    'participants.experience': 'Experience',
    'participants.gender': 'Gender',
    'participants.notes': 'Notes',
    'participants.edit': 'Edit Participant',
    'participants.new': 'New Participant',
    'participants.delete_confirm': 'Really delete this participant?',
    'participants.no_results': 'No participants found',
    'participants.total': '{count} participants',
    'participants.count': '{filtered} of {total} participants',
    'participants.payment': 'Payment',
    'participants.registration_status': 'Registration Status',
    'participants.diet': 'Diet',
    'participants.add_btn': 'Add',
    'participants.set_status': 'Set Status',
    'participants.set_payment': 'Set Payment',
    'participants.assign_team': 'Assign Team',
    'participants.no_team': 'No Team',
    'participants.grid_edit': 'Edit Grid',
    'participants.end_grid_edit': 'End Editing',
    'participants.selected': '{n} selected',
    'participants.actions': 'Actions',
    'participants.page_of': 'Page {page} of {total}',
    'participants.save_view': 'Save View',
    'participants.view_name': 'View Name',
    'participants.add_modal_title': 'Add Participant',
    'participants.personal_data': 'Personal Data',
    'participants.baseball': 'Baseball',
    'participants.contact': 'Contact',
    'participants.health': 'Health',
    'participants.status_payment': 'Status & Payment',
    'participants.internal_notes': 'Internal Notes',
    'participants.notes_placeholder': 'Visible to staff only…',
    'participants.first_name_label': 'First Name',
    'participants.last_name_label': 'Last Name',
    'participants.email': 'Email',
    'participants.phone': 'Phone',
    'participants.dob': 'Date of Birth',
    'participants.shirt': 'T-Shirt',
    'participants.club': 'Club',
    'participants.play_position': 'Playing Position',
    'participants.level': 'Level',
    'participants.height': 'Height (cm)',
    'participants.weight': 'Weight (kg)',
    'participants.emergency_contact': 'Emergency Contact Name',
    'participants.emergency_phone': 'Emergency Contact Phone',
    'participants.allergies': 'Allergies',
    'participants.nutrition': 'Diet',
    'participants.medical': 'Medical Notes',
    'participants.parent_name': 'Parent Name',
    'participants.parent_phone': 'Parent Phone',
    'participants.parent_email': 'Parent Email',
    'participants.amount': 'Amount (€)',
    'participants.amount_paid': 'Paid (€)',
    'participants.date': 'Date',
    'participants.delete_confirm_bulk': 'Really delete {n} participants?',
    'participants.delete_confirm_single': 'Really delete this participant?',
    // Registration status labels
    'participants.status.new': 'New Registration',
    'participants.status.incomplete': 'Incomplete',
    'participants.status.confirmed': 'Confirmed',
    'participants.status.waitlist': 'Waitlist',
    'participants.status.cancelled': 'Cancelled',
    'participants.status.checked_in': 'Checked In',
    'participants.status.departed': 'Departed',
    // Payment status labels
    'participants.payment.open': 'Open',
    'participants.payment.partial': 'Partial',
    'participants.payment.paid': 'Paid',
    'participants.payment.discounted': 'Discounted',
    'participants.payment.free': 'Free Spot',
    'participants.payment.cash': 'Cash Paid',
    'participants.payment.transfer': 'Transferred',
    // Gender labels
    'participants.gender.male': 'Male',
    'participants.gender.female': 'Female',
    'participants.gender.other': 'Other',
    // Experience labels
    'participants.exp.beginner': 'Beginner',
    'participants.exp.intermediate': 'Intermediate',
    'participants.exp.advanced': 'Advanced+',
    'participants.exp.pro': 'Pro',

    // Registration status
    'reg.status.registered': 'Registered',
    'reg.status.confirmed': 'Confirmed',
    'reg.status.waitlist': 'Waitlist',
    'reg.status.cancelled': 'Cancelled',

    // Payment
    'payment.open': 'Open',
    'payment.paid': 'Paid',
    'payment.partial': 'Partial',
    'payment.refunded': 'Refunded',

    // Gender
    'gender.male': 'Male',
    'gender.female': 'Female',
    'gender.diverse': 'Diverse',
    'gender.unknown': 'Unknown',

    // Experience
    'exp.beginner': 'Beginner',
    'exp.intermediate': 'Intermediate',
    'exp.advanced': 'Advanced',
    'exp.elite': 'Elite',

    // --- Teams ---
    'teams.title': 'Teams',
    'teams.add': '+ Add Team',
    'teams.new': 'New Team',
    'teams.edit': 'Edit Team',
    'teams.name': 'Team Name',
    'teams.color': 'Team Color',
    'teams.timeslot': 'Time Slot',
    'teams.logo': 'Logo',
    'teams.photo': 'Team Photo',
    'teams.photo_optional': 'Team Photo (optional)',
    'teams.description': 'Description (optional)',
    'teams.no_teams': 'No teams available',
    'teams.coach': 'Coach',
    'teams.players': 'Players',
    'teams.no_players': 'No players assigned',
    'teams.morning': 'Morning',
    'teams.afternoon': 'Afternoon',
    'teams.cheerleader': 'Cheerleader',
    'teams.players_tab': 'Players',
    'teams.attendance_tab': 'Attendance',
    'teams.delete_confirm': 'Really delete this team?',
    'teams.delete_confirm_full': 'Really delete this team? Participants will be unassigned.',
    'teams.subtitle': '{count} Teams · {participants} Participants',
    'teams.tab_manage': 'Teams',
    'teams.tab_assign': 'Assignment',
    'teams.tab_balance': 'Balance',
    'teams.create_btn': 'Create Team',
    'teams.none_yet': 'No teams yet',
    'teams.none_yet_desc': 'Create the first team!',
    'teams.avg_age': 'Avg Age',
    'teams.avg_height': 'Avg cm',
    'teams.coaches': 'Coaches',
    'teams.error_load': 'Error loading',
    'teams.error_save': 'Error saving',
    'teams.modal_create': 'Create Team',
    'teams.modal_edit': 'Edit Team',
    'teams.photo_hero': 'Team Photo (Hero)',
    'teams.search_coach': 'Search person…',
    'teams.no_coach_result': 'No results',
    'teams.auto_assign': 'Auto-Assignment',
    'teams.save_changes': 'Save changes',
    'teams.create_first': 'First create teams in the "Teams" tab.',
    'teams.no_team_label': 'No Team',
    'teams.search_participant': 'Search participant…',
    'teams.all_assigned': 'All assigned',
    'teams.no_results_search': 'No matches',
    'teams.drop_hint': 'Drag participants here to assign',
    'teams.no_data': '(no data)',
    'teams.calc': 'Calculating…',
    'teams.distribute': 'Distributing…',
    'teams.distribute_n': 'Assign {n} participants',
    'teams.no_teams_yet': 'No teams yet.',
    'teams.slot_morning': 'Morning',
    'teams.slot_afternoon': 'Afternoon',
    'teams.unassigned_warning': '{n} participants not yet assigned to a team.',
    'teams.criterion_age': 'Age',
    'teams.criterion_height': 'Height',
    'teams.criterion_weight': 'Weight',
    'teams.criterion_experience': 'Experience',
    'teams.criterion_gender': 'Gender',
    'teams.auto_desc': 'Distribute {n} participants across {teams} teams. Choose criteria for balanced distribution (Snake Draft):',
    'teams.tn': 'P',
    'teams.no_members': 'No players in this team',
    'teams.col_year': 'Year',
    'teams.col_shirt': 'Shirt',
    'teams.choose_image': 'Choose image',
    'teams.logo_upload': 'Logo',

    // --- Attendance ---
    'att.title': 'Attendance',
    'att.present': 'Present',
    'att.absent': 'Absent',
    'att.late': 'Late',
    'att.excused': 'Excused',
    'att.not_recorded': '— Not recorded',
    'att.click_hint': 'Click on the status to change it',
    'att.date': 'Date',
    'att.all_present': 'All present',
    'att.overview': 'Overview',

    // --- Check-in ---
    'checkin.title': 'Check-In',
    'checkin.search': 'Search participant...',
    'checkin.checked_in': 'Checked In',
    'checkin.not_checked_in': 'Not checked in',
    'checkin.all': 'All',
    'checkin.check_in': 'Check In',
    'checkin.check_out': 'Check Out',
    'checkin.no_results': 'No results',
    'checkin.total': '{checked}/{total} checked in',

    // --- Tournament ---
    'tourn.title': 'Tournament',
    'tourn.add_match': '+ Add Match',
    'tourn.no_matches': 'No tournament schedule',
    'tourn.live_matches': 'LIVE GAMES',
    'tourn.scheduled': 'Scheduled',
    'tourn.live': 'LIVE',
    'tourn.finished': 'Finished',
    'tourn.field': 'Field',
    'tourn.round': 'Round',
    'tourn.edit_score': 'Edit Score',
    'tourn.live_score': 'Live Score',
    'tourn.score_status': 'Score / Status',
    'tourn.add_match_title': 'Add Match',
    'tourn.edit_match_title': 'Edit Match',
    'tourn.team1': 'Team 1',
    'tourn.team2': 'Team 2',
    'tourn.tbd': '— TBD —',
    'tourn.sort_order': 'Sort Order',
    'tourn.delete_confirm': 'Really delete this match?',
    'tourn.vs': 'vs.',
    'tourn.live_ticker': 'Live Ticker',
    'tourn.no_matches_title': 'No matches yet',
    'tourn.no_matches_desc': 'Generate a round-robin or add individual matches.',
    'tourn.generate_btn': 'Generate Round-Robin',
    'tourn.match_short': 'Match',
    'tourn.generating': 'Generating…',
    'tourn.generate_confirm': 'Delete all existing matches and regenerate round-robin?',
    'tourn.generated_msg': '{count} matches in {rounds} rounds generated.',
    'tourn.generate_error': 'Error generating',
    'tourn.tab_standings': 'Standings',
    'tourn.tab_schedule': 'Schedule',
    'tourn.tab_bracket': 'Bracket',
    'tourn.tab_results': 'Results',
    'tourn.no_matches_empty': 'No matches',
    'tourn.no_results': 'No results yet',
    'tourn.round_n': 'Round {n}',
    'tourn.matches_count': '{n} matches',
    'tourn.col_games': 'G',
    'tourn.col_wins': 'W',
    'tourn.col_ties': 'T',
    'tourn.col_losses': 'L',
    'tourn.col_points': 'Points',
    'tourn.field_n': 'Field {n}',
    'tourn.ago_now': 'just now',
    'tourn.ago_min': '{n}m ago',
    'tourn.ago_hour': '{n}h ago',
    'tourn.home_team': 'Home Team',
    'tourn.away_team': 'Away Team',
    'tourn.select_team': 'Select team…',
    'tourn.kickoff': 'Kickoff',
    'tourn.new_match': 'New Match',
    'tourn.live_end': 'End Live',
    'tourn.live_start': 'Mark as Live',
    'tourn.stat_teams': 'Teams',
    'tourn.stat_matches': 'Matches',
    'tourn.delete_match_confirm': 'Delete match?',

    // --- Schedule ---
    'schedule.title': 'Daily Schedule',
    'schedule.add_item': '+ Add Item',
    'schedule.no_schedule': 'No schedule added yet',
    'schedule.time': 'Time',
    'schedule.activity': 'Activity',
    'schedule.view': '→ View Schedule',
    'schedule.delete_confirm': 'Really delete this item?',
    'schedule.add_entry': 'Add Entry',
    'schedule.add_day': 'Day',
    'schedule.no_days': 'No days added yet',
    'schedule.no_days_desc': 'Add a day using the "+ Day" button.',
    'schedule.no_entries': 'No entries for this day',
    'schedule.no_entries_desc': 'Add the first program item.',
    'schedule.edit_entry': 'Edit Entry',
    'schedule.new_entry': 'New Entry',
    'schedule.type_entry': 'Program Item',
    'schedule.type_header': 'Section Header',
    'schedule.type_divider': 'Divider',
    'schedule.color': 'Color',
    'schedule.start_time': 'Start Time',
    'schedule.duration_min': 'Duration (minutes)',
    'schedule.location': 'Location',
    'schedule.responsible': 'Responsible',
    'schedule.delete_title': 'Delete Entry',
    'schedule.delete_msg': 'Really delete this entry?',
    'schedule.delete_day_confirm': 'Delete this day and all entries?',

    // --- Evenings ---
    'evenings.title': 'Camp Evenings',
    'evenings.add': '+ Add Evening',
    'evenings.no_evenings': 'No evenings planned',
    'evenings.no_evenings_desc': 'Evening programs will appear here.',
    'evenings.edit': 'Edit Evening',
    'evenings.new': 'New Evening',
    'evenings.date': 'Date',
    'evenings.schedule': 'Schedule',
    'evenings.add_schedule_item': '+ Add Item',
    'evenings.no_schedule': 'No schedule added yet',
    'evenings.view_schedule': '→ View Schedule',
    'evenings.delete_confirm': 'Really delete this evening?',
    'evenings.edit_evening': 'Edit Evening',
    'evenings.evening_name': 'Evening Name',
    'evenings.banner': 'Banner Image',
    'evenings.upload_image': 'Upload Image',
    'evenings.replace_image': 'Replace Image',
    'evenings.uploading': 'Uploading…',

    // --- Teamtimes ---
    'teamtimes.title': 'Team Times',
    'teamtimes.add': '+ Add Team Time',
    'teamtimes.no_items': 'No team times available',
    'teamtimes.edit': 'Edit Team Time',
    'teamtimes.new': 'New Team Time',
    'teamtimes.topic': 'Topic',
    'teamtimes.team': 'Team',
    'teamtimes.delete_confirm': 'Really delete this team time?',
    'teamtimes.field_title': 'Title',
    'teamtimes.field_date': 'Date',
    'teamtimes.field_audience': 'Target Audience',
    'teamtimes.field_theme': 'Theme',
    'teamtimes.field_bible': 'Bible Reference',
    'teamtimes.field_content': 'Content',
    'teamtimes.field_questions': 'Discussion Questions',
    'teamtimes.field_staff': 'Staff Notes (internal)',
    'teamtimes.section_content': 'Content',
    'teamtimes.section_questions': 'Discussion Questions',
    'teamtimes.section_staff': 'Staff Notes',
    'teamtimes.attachments': 'Attachments',
    'teamtimes.no_attachments': 'No attachments',
    'teamtimes.attach_file': 'Attach File',
    'teamtimes.uploading': 'Uploading…',
    'teamtimes.theme_prefix': 'Theme:',

    // --- Questions ---
    'questions.title': 'Question of the Day',
    'questions.add': '+ Add Question',
    'questions.no_items': 'No questions available',
    'questions.edit': 'Edit Question',
    'questions.new': 'New Question',
    'questions.question': 'Question',
    'questions.date': 'Date',
    'questions.delete_confirm': 'Really delete this question?',

    // --- Staff ---
    'staff.title': 'Staff',
    'staff.add': '+ Add Staff',
    'staff.no_staff': 'No staff members',
    'staff.edit': 'Edit Staff',
    'staff.new': 'New Staff Member',
    'staff.role': 'Role',
    'staff.team': 'Team',
    'staff.delete_confirm': 'Really remove this staff member?',
    'staff.delete_title': 'Delete Staff Member',
    'staff.delete_msg': 'Really delete this staff member?',
    'staff.name': 'Name',
    'staff.phone': 'Phone',
    'staff.special_tasks': 'Special Tasks',
    'staff.availability': 'Availability / Notes',
    'staff.count': '{filtered} of {total} members',
    'staff.not_found': 'No staff members found',
    'staff.no_staff_yet': 'No staff yet',
    'staff.no_staff_yet_desc': 'Add the first staff member.',
    'staff.grid_edit': 'Edit Grid',
    'staff.end_grid_edit': 'End Editing',
    'staff.new_member_ghost': 'New Staff Member',

    // --- ToDos ---
    // --- Attendance ---
    // --- Checkin ---
    'checkin.title': 'Check-In',
    'checkin.checked_in_of': '{checkedIn} of {total} checked in',
    'checkin.total': 'Total',
    'checkin.checked_in': 'Checked In',
    'checkin.pending': 'Pending',
    'checkin.checked_in_label': '{n} checked in',
    'checkin.search': 'Search name…',
    'checkin.no_results': 'Nothing found',
    'checkin.no_results_search': 'No results for "{q}"',
    'checkin.no_participants': 'No participants yet',
    'checkin.undo': 'Undo',
    'checkin.check_in_btn': 'Check In',

    'attendance.title': 'Attendance',
    'attendance.present_of': '{present} / {total} present',
    'attendance.saved_at': 'Saved {time}',
    'attendance.all_present': 'All present',
    'attendance.all_absent': 'All absent',
    'attendance.present': 'Present',
    'attendance.absent': 'Absent',
    'attendance.none': 'None',
    'attendance.no_team': 'No Team',
    'attendance.no_participants': 'No participants found',

    'todos.title': 'To-Dos',
    'todos.add': '+ Add To-Do',
    'todos.no_todos': 'No to-dos available',
    'todos.open': 'Open',
    'todos.done': 'Done',
    'todos.all': 'All',
    'todos.new': 'New To-Do',
    'todos.edit': 'Edit To-Do',
    'todos.task': 'Task',
    'todos.assignee': 'Assignee',
    'todos.due_date': 'Due Date',
    'todos.priority': 'Priority',
    'todos.delete_confirm': 'Really delete this to-do?',
    'todos.open_count': '{count} open',
    'todos.stat_line': '{open} open · {done} done',
    'todos.no_open': 'No open to-dos',
    'todos.nothing_done': 'Nothing completed yet',
    'todos.title_label': 'Title',
    'todos.description': 'Description',
    'todos.due': 'Due',
    'todos.assigned_to': 'Assigned to',
    'todos.prio_high': 'High',
    'todos.prio_medium': 'Medium',
    'todos.prio_low': 'Low',
    'todos.prio_normal': 'Normal',
    'todos.prio_urgent': 'Urgent',

    // --- Kanban Board ---
    'todos.boards': 'Boards',
    'todos.new_board': 'New Board',
    'todos.no_boards': 'No boards yet',
    'todos.no_boards_yet': 'Create your first board to get started.',
    'todos.create_first_board': 'Create First Board',
    'todos.board_name': 'Board Name',
    'todos.board_desc': 'Description (optional)',
    'todos.board_settings': 'Board Settings',
    'todos.add_list': '+ Add List',
    'todos.add_card': '+ Add Card',
    'todos.list_name': 'List Name',
    'todos.card_title': 'Card Title',
    'todos.view_kanban': 'Kanban',
    'todos.view_list': 'Table',
    'todos.view_calendar': 'Calendar',
    'todos.view_my_tasks': 'My Tasks',
    'todos.labels': 'Labels',
    'todos.members': 'Members',
    'todos.start_date': 'Start Date',
    'todos.cover_color': 'Cover Color',
    'todos.checklist': 'Checklist',
    'todos.add_checklist': '+ Add Checklist',
    'todos.checklist_name': 'Checklist Name',
    'todos.add_item': '+ Add Item',
    'todos.checklist_progress': '{done} / {total} done',
    'todos.comments': 'Comments',
    'todos.write_comment': 'Write a comment...',
    'todos.activity': 'Activity',
    'todos.move_to_list': 'Move to List',
    'todos.archive_card': 'Archive Card',
    'todos.archive_board': 'Archive Board',
    'todos.templates': 'Templates',
    'todos.save_as_template': 'Save as Template',
    'todos.load_template': 'Load Template',
    'todos.filter_assignee': 'Member',
    'todos.filter_priority': 'Priority',
    'todos.filter_label': 'Label',
    'todos.filter_due': 'Due Date',
    'todos.my_tasks': 'My Tasks',
    'todos.overdue': 'Overdue',
    'todos.today': 'Today',
    'todos.upcoming': 'Upcoming',
    'todos.no_date': 'No Date',
    'todos.today_section': 'Due Today',
    'todos.overdue_section': 'Overdue',
    'todos.upcoming_section': 'Upcoming',
    'todos.no_cards': 'No Cards',
    'todos.no_my_tasks': 'No tasks assigned to you.',
    'todos.card_archived': 'Card archived',
    'todos.board_archived': 'Board archived',
    'todos.list_archived': 'List archived',
    'todos.duplicate_board': 'Duplicate Board',
    'todos.rename_list': 'Rename List',
    'todos.delete_list': 'Archive List',
    'todos.mark_done_list': 'Mark as Done List',
    'todos.activity_created': 'created this card',
    'todos.activity_moved': 'moved this card',
    'todos.activity_commented': 'commented',
    'todos.activity_archived': 'archived this card',
    'todos.activity_assigned': 'changed members',
    'todos.activity_due_date_changed': 'changed the due date',
    'todos.activity_label_changed': 'changed labels',
    'todos.no_description': 'No description added.',
    'todos.edit_description': 'Edit description...',
    'todos.cover_none': 'No Cover',
    'todos.export_csv': 'Export as CSV',
    'todos.export_xlsx': 'Export as Excel',
    'todos.template_name': 'Template Name',
    'todos.apply_template': 'Apply Template',
    'todos.done_list_badge': 'Done List',
    'todos.list_has_cards': 'This list still has active cards.',

    // --- Lists ---
    'lists.title': 'Lists',
    'lists.add': '+ Add List',
    'lists.no_lists': 'No lists available',
    'lists.no_lists_hint': 'No lists yet.\nClick "New List".',
    'lists.edit': 'Edit List',
    'lists.new': 'New List',
    'lists.new_short': 'New',
    'lists.delete_confirm': 'Really delete this list?',
    'lists.delete_list_title': 'Delete List',
    'lists.delete_list_msg': 'This list and all rows will be permanently deleted.',
    'lists.delete_row_title': 'Delete Row',
    'lists.delete_row_msg': 'This row will be permanently deleted.',
    'lists.add_item': '+ Item',
    'lists.items': 'Items',
    'lists.entries': 'entries',
    'lists.of': 'of',
    'lists.select_list': 'Select List',
    'lists.select_list_desc': 'Select a list on the left or create a new one.',
    'lists.select_mobile': 'Choose list',
    'lists.add_row': 'Add',
    'lists.new_row': 'New Row',
    'lists.edit_row': 'Edit Row',
    'lists.columns_btn': 'Columns',
    'lists.manage_columns': 'Manage Columns',
    'lists.no_columns': 'No columns defined yet',
    'lists.no_columns_desc': 'Click "Columns" to add columns.',
    'lists.grid_done': 'Done',
    'lists.grid_mode': 'Grid',
    'lists.selected_n': '{n} selected',
    'lists.empty_rows': 'No entries — click "+ Add"',
    'lists.no_results_search': 'No results',
    'lists.template': 'Use Template',
    'lists.template_empty': 'Blank (no template)',
    'lists.row_no_columns': 'No columns defined yet.',
    'lists.no_options': 'No options defined — please add in column settings.',
    'lists.person_placeholder': 'Search person…',
    'lists.no_selection': '— no selection —',
    'lists.icon': 'Icon',

    // --- Files ---
    'files.title': 'Files',
    'files.upload': '+ Upload File',
    'files.no_files': 'No files available',
    'files.delete_confirm': 'Really delete this file?',
    'files.folder': 'Folder',
    'files.new_folder': 'New Folder',
    'files.file_name': 'File Name',
    'files.size': 'Size',
    'files.uploaded_at': 'Uploaded at',
    'files.file': 'File',
    'files.files': 'Files',
    'files.upload_btn': 'Upload',
    'files.upload_error': 'Upload failed. Please try again.',
    'files.drop_hint': 'Drop files here to upload · max. 50 MB per file',
    'files.select_btn': 'Browse',
    'files.no_results': 'No results',
    'files.no_results_for': 'No results for',
    'files.empty_desc': 'Drop files here or click "Upload"',
    'files.rename_folder': 'Rename Folder',
    'files.new_name': 'New Name',
    'files.delete_file_title': 'Delete File',
    'files.delete_file_msg': 'This file will be permanently deleted.',
    'files.delete_folder_title': 'Delete Folder',
    'files.delete_folder_msg': 'This folder and all its contents will be permanently deleted.',
    'files.items_count': 'Items',
    'files.items_short': 'Items',
    'files.no_entries': 'No entries found',
    'files.selected': 'selected',
    'files.list_view': 'List view',
    'files.grid_view': 'Grid view',
    'files.col_type': 'Type',
    'files.folder_type': 'Folder',

    // --- Meals ---
    'meals.title': 'Kitchen & Meals',
    'meals.add': '+ Meal',
    'meals.no_meals': 'No meals yet',
    'meals.edit': 'Edit Meal',
    'meals.new': 'New Meal',
    'meals.meal': 'Meal',
    'meals.date': 'Date',
    'meals.portions': 'Portions',
    'meals.delete_confirm': 'Delete meal?',
    'meals.shopping_list': 'Shopping List',
    'meals.no_date': 'No Date',
    'meals.field_title': 'Title',
    'meals.field_type': 'Type',
    'meals.notes_ingredients': 'Notes / Ingredients',
    'meals.type.breakfast': 'Breakfast',
    'meals.type.lunch': 'Lunch',
    'meals.type.dinner': 'Dinner',
    'meals.type.snack': 'Snack',

    // --- Finance ---
    'finance.title': 'Finance',
    'finance.add': '+ Add Entry',
    'finance.no_items': 'No entries available',
    'finance.edit': 'Edit Entry',
    'finance.new': 'New Entry',
    'finance.income': 'Income',
    'finance.expense': 'Expense',
    'finance.balance': 'Balance',
    'finance.amount': 'Amount',
    'finance.category': 'Category',
    'finance.description': 'Description',
    'finance.date': 'Date',
    'finance.delete_confirm': 'Really delete this entry?',

    // --- Equipment ---
    'equipment.title': 'Equipment',
    'equipment.add': '+ Add Equipment',
    'equipment.no_items': 'No equipment available',
    'equipment.edit': 'Edit Equipment',
    'equipment.new': 'New Equipment',
    'equipment.item': 'Item',
    'equipment.quantity': 'Quantity',
    'equipment.condition': 'Condition',
    'equipment.location': 'Location',
    'equipment.delete_confirm': 'Really delete this equipment?',
    'equipment.col_category': 'Category',
    'equipment.col_notes': 'Notes',
    'equipment.cond.new': 'New',
    'equipment.cond.good': 'Good',
    'equipment.cond.fair': 'Fair',
    'equipment.cond.poor': 'Poor',

    // --- Downloads / Flyer ---
    'downloads.title': 'Downloads',
    'downloads.add': '+ Add File',
    'downloads.no_items': 'No downloads available',
    'downloads.edit': 'Edit Entry',
    'downloads.new': 'New Entry',
    'downloads.delete_confirm': 'Really delete this download?',
    'downloads.file': 'File',
    'downloads.category': 'Category',
    'downloads.subtitle': 'Files for the public downloads page',
    'downloads.add_file': 'Add File',
    'downloads.no_items_desc': 'Add flyers, forms and other materials here.',
    'downloads.file_available': 'File attached',
    'downloads.public': 'Public',
    'downloads.hidden': 'Hidden',
    'downloads.visibility': 'Visibility',
    'downloads.public_visible': 'Publicly visible',
    'downloads.select_file': 'Select File',
    'downloads.edit_file': 'Edit File',
    'downloads.new_file': 'New File',
    'downloads.toggle_show': 'Public – click to hide',
    'downloads.toggle_hide': 'Hidden – click to publish',
    'downloads.delete_file_confirm': 'Delete this file?',

    // --- Statistics ---
    'stats.title': 'Statistics',
    'stats.subtitle': 'Camp overview',
    'stats.participants': 'Participants',
    'stats.checked_in': 'Checked In',
    'stats.teams': 'Teams',
    'stats.paid': 'Paid',
    'stats.today_present': 'Present today',
    'stats.open_todos': 'Open To-Dos',
    'stats.attendance_rate': 'Attendance rate',
    'stats.payment_rate': 'Payment rate',
    'stats.progress': 'Progress',
    'stats.att_week': 'Attendance – last 7 days',
    'stats.team_sizes': 'Team Sizes',
    'questions.answer_label': 'Answer',
    'questions.answer_placeholder': 'Enter answer...',
    'questions.answer_btn': 'Reply',
    'questions.no_questions': 'No questions yet',

    // --- Dashboard ---
    'dash.to_camp': 'Camp Page',
    'dash.settings': 'Settings',

    // --- Forms / Registration ---
    'forms.title': 'Forms',
    'forms.registration': 'Registration Form',
    'forms.add': '+ Add Form',
    'forms.no_forms': 'No forms available',
    'forms.edit': 'Edit Form',
    'forms.new': 'New Form',
    'forms.field': 'Field',
    'forms.label': 'Label',
    'forms.required': 'Required',
    'forms.delete_confirm': 'Really delete this form?',
    'forms.responses': 'Responses',
    'forms.no_responses': 'No responses yet',
    'forms.subtitle': 'Create and manage registration forms for your camp',
    'forms.no_forms_title': 'No forms yet',
    'forms.no_forms_desc': 'Create your first registration form.',
    'forms.create': 'Create Form',
    'forms.active': 'Active',
    'forms.inactive': 'Inactive',
    'forms.fields_count': '{n} fields',
    'forms.submissions_count': '{n} submissions',
    'forms.submissions': 'Submissions',
    'forms.delete_form_confirm': 'Delete form and all fields?',
    'forms.load_error': 'Could not load form.',
    'forms.create_error': 'Error creating form.',
    'forms.saved': 'Saved ✓',
    'forms.save_error': 'Error!',
    'forms.unsaved_confirm': 'Discard unsaved changes?',
    'forms.back': 'Back',
    'forms.back_to_builder': 'Back to Builder',
    'forms.preview_label': 'Preview',
    'forms.saving': 'Saving…',
    'forms.save_dirty': 'Save*',
    'forms.saved_btn': 'Saved',
    'forms.description_label': 'Description',
    'forms.description_placeholder': 'Short description of the form (publicly visible)',
    'forms.confirmation_label': 'Confirmation Text',
    'forms.confirmation_placeholder': 'Text after successful registration...',
    'forms.button_color': 'Button Color',
    'forms.add_fields': 'Add Fields',
    'forms.add_field_btn': '+ Add Field',
    'forms.field_count': '{n} fields',
    'forms.canvas_empty': 'Add fields on the left to build your form.',
    'forms.select_field': 'Select a field to edit',
    'forms.activate_title': 'Activate form (make publicly accessible)',
    'forms.drag_hint': 'Drag to reorder',
    'forms.move_up': 'Move up',
    'forms.move_down': 'Move down',
    'forms.name_label': 'Form Name *',
    'forms.name_placeholder': 'e.g. Baseball Camp 2026 Registration',
    'forms.submit_preview': 'Register → (Preview)',
    'forms.select_placeholder': 'Please select…',
    'forms.submissions_title': 'Submissions: {name}',
    'forms.submissions_count_msg': '{n} submissions received',
    'forms.no_submissions': 'No submissions yet',
    'forms.no_submissions_desc': 'Once someone fills out the form, they will appear here.',
    'forms.col_name': 'Name',
    'forms.col_email': 'E-Mail',
    'forms.col_status': 'Status',
    'forms.col_registered': 'Registered on',
    'forms.fe_edit_section': 'Edit Section',
    'forms.fe_section_title': 'Title',
    'forms.fe_section_desc': 'Description (optional)',
    'forms.fe_section_desc_placeholder': 'Short explanation for this section',
    'forms.fe_delete_section': 'Delete Section',
    'forms.fe_edit_field': 'Edit Field',
    'forms.fe_field_type': 'Field Type',
    'forms.fe_label_de': 'Label (DE) *',
    'forms.fe_label_en': 'Label (EN, optional)',
    'forms.fe_help_text': 'Help Text',
    'forms.fe_help_placeholder': 'Shown below the field...',
    'forms.fe_placeholder': 'Placeholder',
    'forms.fe_required': 'Required',
    'forms.fe_mapping': 'Mapping → Participant Field',
    'forms.fe_mapping_desc': 'Automatically assigns the answer to the participant profile.',
    'forms.fe_options': 'Options',
    'forms.fe_option_label': 'Label',
    'forms.fe_option_value': 'Value',
    'forms.fe_add_option': '+ Option',
    'forms.fe_delete_field': 'Delete Field',
    'forms.ft_text': 'Text field',
    'forms.ft_textarea': 'Multiline Text',
    'forms.ft_email': 'E-Mail',
    'forms.ft_phone': 'Phone number',
    'forms.ft_date': 'Date',
    'forms.ft_number': 'Number field',
    'forms.ft_select': 'Select field',
    'forms.ft_multiselect': 'Multiple choice',
    'forms.ft_checkbox': 'Checkbox',
    'forms.ft_yesno': 'Yes / No',

    // --- Camp Admin Settings ---
    'admin.title': 'Camp Settings',
    'admin.modules': 'Modules',
    'admin.modules_desc': 'Enable or disable features for this camp.',
    'admin.branding': 'Design & Branding',
    'admin.general': 'General',
    'admin.users': 'Users',
    'admin.users_add': '+ Create User',
    'admin.users_edit': 'Edit User',
    'admin.users_new': 'New User',
    'admin.users_search': 'Search users...',
    'admin.username': 'Username',
    'admin.display_name': 'Display Name',
    'admin.email': 'Email',
    'admin.role': 'Role',
    'admin.roles': 'Roles',
    'admin.assign_teams': 'Assign Teams',
    'admin.account_active': 'Account active',
    'admin.active': 'Active',
    'admin.inactive': 'Inactive',
    'admin.delete_user_confirm': 'Really delete this user?',
    'admin.permissions': 'Permissions',
    'admin.simulate': 'Simulate as this user',
    'admin.tab_general': 'General',
    'admin.tab_camps': 'Camps',
    'admin.tab_users': 'Users',
    'admin.tab_permissions': 'Roles & Permissions',
    'admin.tab_branding': 'Branding & Colors',
    'admin.tab_cms': 'Landing Page',
    'admin.tab_modules': 'Modules',
    'admin.general_title': 'Camp Info',
    'admin.general_readonly': 'Camp details can only be edited by superadmins.',
    'admin.field_location': 'Location',
    'admin.field_start': 'Start',
    'admin.field_end': 'End',
    'admin.camps_manage': 'Manage the annual instances of this camp',
    'admin.camps_org': 'Organization: {name}',
    'admin.new_camp': 'New Camp',
    'admin.no_camps': 'No other camps in this tenant yet',
    'admin.camp_current': 'Current',
    'admin.camp_active': 'Active',
    'admin.camp_activate': 'Activate',
    'admin.camp_delete_title': 'Delete Camp',
    'admin.camp_delete_msg': 'This camp and all associated participants will be permanently deleted. This action cannot be undone.',
    'admin.slug_hint': '(URL identifier)',
    'admin.field_year': 'Year',
    'admin.creating': 'Creating…',
    'admin.create_camp': 'Create Camp',
    'admin.users_section_title': 'Camp Users',
    'admin.users_desc': 'Manage access and roles for this camp',
    'admin.no_users': 'No users in this camp yet',
    'admin.password': 'Password',
    'admin.remove_user_title': 'Remove User',
    'admin.remove_user_msg': 'Remove this user from the camp? The account will remain.',
    'admin.remove': 'Remove',
    'admin.roles_label': 'Roles',
    'admin.new_role_title': 'Create New Role',
    'admin.role_display_name': 'Display Name',
    'admin.role_key': 'Role Key',
    'admin.role_key_hint': '(only a-z, 0-9, _)',
    'admin.create_role': 'Create Role',
    'admin.select_role_hint': 'Select a role to edit its permissions.',
    'admin.role_delete_confirm': 'Delete role "{name}"?',
    'admin.branding_title': 'Logo & Theme',
    'admin.branding_desc': 'Customize the logo and colors of the camp environment to match your CI. Changes affect the internal app, public page, and all camp areas.',
    'admin.logo_section': 'Camp Logo',
    'admin.logo_hint': 'Used in the header and as PWA icon. Recommended: square, min. 512×512px.',
    'admin.logo_upload': 'Upload logo',
    'admin.logo_change': 'Change logo',
    'admin.logo_remove': 'Remove',
    'admin.logo_uploading': 'Uploading…',
    'admin.live_preview': 'Live Preview',
    'admin.primary_color': 'Primary Color',
    'admin.primary_hint': 'Buttons, active navigation, accents — the main color of your camp.',
    'admin.secondary_color': 'Secondary Color',
    'admin.secondary_hint': 'Second accent color for highlights and badges.',
    'admin.accent_color': 'Accent',
    'admin.accent_hint': 'Third color for tabs and special markings.',
    'admin.save_branding': 'Save Theme',
    'admin.saved_branding': 'Theme saved!',
    'admin.cms_title': 'Public Page',
    'admin.hero_tagline': 'Hero Tagline',
    'admin.hero_subtitle': 'Hero Subtitle',
    'admin.camp_type': 'Camp Type',
    'admin.age_group': 'Age Group',
    'admin.price_info': 'Price Info',
    'admin.save_cms': 'Save Landing Page',
    'admin.saved_cms': 'Landing page saved!',
    'admin.modules_title': 'Modules',
    'admin.save_modules': 'Save Modules',
    'admin.saved_modules': 'Modules saved!',
    'admin.saved_role': 'Role saved!',
    'admin.error_activate': 'Error activating',
    'admin.error_create': 'Error creating',
    'admin.error_simulate': 'Error simulating',
    'admin.color_reset': 'Reset',
    'admin.adding': 'Add',
    'admin.tab_push': 'Push',
    'admin.tab_registrations': 'Registrations',
    'admin.reg_empty': 'No registration requests',
    'admin.reg_approve': 'Approve',
    'admin.reg_reject': 'Reject',
    'admin.reg_approved': 'Approved',
    'admin.reg_rejected': 'Rejected',
    'admin.reg_pending': 'Pending',
    'admin.reg_message': 'Message',
    'admin.reg_requested_at': 'Requested at',
    'admin.push_subscribers': 'Subscribers',
    'admin.push_send_title': 'Send Notification',
    'admin.push_title_label': 'Title',
    'admin.push_title_placeholder': 'e.g. New announcement from camp',
    'admin.push_body_label': 'Message',
    'admin.push_body_placeholder': 'Message that users will receive...',
    'admin.push_url_label': 'Link (optional)',
    'admin.push_url_hint': 'Where should a click on the notification lead?',
    'admin.push_send_btn': 'Send to',
    'admin.push_sending': 'Sending...',
    'admin.push_sent': '{sent} of {total} sent successfully',
    'admin.push_no_subscribers': 'No subscribers — activate first on the news page',
    'admin.push_auto_title': 'Automatic Article Notification',
    'admin.push_auto_desc': 'Sent automatically when an article is published.',
    'admin.push_auto_enabled': 'Send automatically',
    'admin.push_auto_title_tpl': 'Title template',
    'admin.push_auto_body_tpl': 'Body template',
    'admin.push_auto_vars': 'Available variables: {title}, {teaser}',
    'admin.push_auto_save': 'Save template',
    'admin.push_auto_saved': 'Saved',
    'admin.session_title': 'Age Group Assignment',
    'admin.session_desc': 'Defines the age at which participants are automatically assigned to morning or afternoon.',
    'admin.morning_max_age': 'Morning – Maximum Age',
    'admin.morning_max_age_desc': 'Participants up to this age are automatically assigned to morning.',
    'admin.afternoon_min_age': 'Afternoon – Minimum Age',
    'admin.afternoon_min_age_desc': 'Participants from this age are automatically assigned to afternoon.',
    'admin.session_auto_note': 'Assignment happens automatically when a participant is created and can be adjusted manually afterwards.',

    // --- SuperAdmin ---
    'sa.dashboard': 'Dashboard',
    'sa.tenants': 'Tenants',
    'sa.camps': 'Camps',
    'sa.users': 'Users',
    'sa.equipment': 'Equipment',
    'sa.templates': 'Templates',
    'sa.backups': 'Backups',
    'sa.settings': 'Settings',
    'sa.logout': 'Sign Out',
    'sa.to_platform': 'To Platform',
    'sa.superadmin': 'SuperAdmin',
    'sa.platform': 'Baseball Camp Platform',
    'sa.create_backup': 'Create Backup',
    'sa.restore': 'Restore',
    'sa.delete_confirm': 'Really delete?',

    // --- Platform landing ---
    'platform.title': 'Baseball Camp Platform',
    'platform.subtitle': 'Management for your camp',
    'platform.admin_login': 'Admin Login',
    'platform.camp_login': 'Camp Login',
    'platform.enter_slug': 'Enter camp slug...',
    'platform.go': 'Go',

    // --- Push notifications ---
    'push.active': 'Push notifications active',
    'push.disable': 'Disable',
    'push.success': 'Notifications enabled!',
    'push.blocked': 'Notifications blocked — enable in browser settings',
    'push.enable_prompt': 'Enable notifications?',
    'push.title': 'Enable Notifications?',
    'push.desc': 'Get instantly notified when there are new updates from camp.',
    'push.enable': 'Enable',
    'push.not_now': 'Not now',
  },

  // =========================================================================
  // SPANISH
  // =========================================================================
  es: {
    // --- Languages ---
    'lang.de': 'Deutsch',
    'lang.en': 'English',
    'lang.es': 'Español',

    // --- Common actions ---
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.create': 'Crear',
    'common.add': 'Agregar',
    'common.close': 'Cerrar',
    'common.back': '← Atrás',
    'common.loading': 'Cargando...',
    'common.saving': 'Guardando...',
    'common.deleting': 'Eliminando...',
    'common.search': 'Buscar...',
    'common.filter': 'Filtrar',
    'common.reset': 'Restablecer',
    'common.columns': 'Columnas',
    'common.confirm': 'Confirmar',
    'common.yes': 'Sí',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.apply': 'Aplicar',
    'common.export': 'Exportar',
    'common.import': 'Importar',
    'common.download': 'Descargar',
    'common.upload': 'Subir',
    'common.rename': 'Renombrar',
    'common.duplicate': 'Duplicar',
    'common.preview': 'Vista previa',
    'common.publish': 'Publicar',
    'common.archive': 'Archivar',
    'common.activate': 'Activar',
    'common.deactivate': 'Desactivar',
    'common.enable': 'Habilitar',
    'common.disable': 'Deshabilitar',
    'common.optional': 'opcional',
    'common.required': 'Requerido',
    'common.all': 'Todos',
    'common.none': 'Ninguno',
    'common.or': 'o',
    'common.and': 'y',
    'common.by': 'por',
    'common.at': 'a las',
    'common.on': 'el',
    'common.from': 'de',
    'common.to': 'hasta',
    'common.of': 'de',
    'common.items': 'elementos',
    'common.actions': 'Acciones',
    'common.status': 'Estado',
    'common.type': 'Tipo',
    'common.name': 'Nombre',
    'common.title': 'Título',
    'common.description': 'Descripción',
    'common.notes': 'Notas',
    'common.date': 'Fecha',
    'common.time': 'Hora',
    'common.email': 'Correo electrónico',
    'common.phone': 'Teléfono',
    'common.address': 'Dirección',
    'common.image': 'Imagen',
    'common.images': 'Imágenes',
    'common.file': 'Archivo',
    'common.files': 'Archivos',
    'common.size': 'Tamaño',
    'common.color': 'Color',
    'common.number': '#',
    'common.total': 'Total',
    'common.count': 'Cantidad',
    'common.yes_delete': 'Sí, eliminar',
    'common.confirm_delete_title': '¿Estás seguro?',
    'common.confirm_delete': '¿Realmente eliminar?',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.warning': 'Advertencia',
    'common.info': 'Info',
    'common.no_access': 'Sin acceso',
    'common.no_access_desc': 'No tienes permiso para ver esta página.',
    'common.not_found': 'No encontrado',
    'common.to_home': 'Ir al inicio',
    'common.copy': 'Copiar',
    'common.copied': '¡Copiado!',
    'common.share': 'Compartir',
    'common.link': 'Enlace',
    'common.read_more': 'Leer más',
    'common.show_less': 'Mostrar menos',

    // --- Roles ---
    'role.admin': 'Admin',
    'role.coach': 'Entrenador',
    'role.social_media': 'Redes Sociales',
    'role.staff': 'Personal',
    'role.viewer': 'Espectador',
    'role.show_master': 'Show Master',
    'role.tournament_manager': 'Director de Torneo',
    'role.superadmin': 'Superadmin',

    // --- Module names ---
    'module.news': 'Noticias',
    'module.teams': 'Equipos',
    'module.tournament': 'Torneo',
    'module.registration': 'Registro',
    'module.flyer': 'Descargas',
    'module.finance': 'Finanzas',
    'module.teamtimes': 'Tiempo de Equipo',
    'module.lists': 'Listas',
    'module.files': 'Archivos',
    'module.todos': 'Tareas',
    'module.meals': 'Cocina',
    'module.staff': 'Personal',
    'module.equipment': 'Equipamiento',
    'module.checkin': 'Check-In',
    'module.schedule': 'Horario',
    'module.evenings': 'Noches',
    'module.questions': 'Pregunta del Día',
    'module.statistics': 'Estadísticas',
    'module.forms': 'Formularios',
    'module.settings': 'Configuración',

    // --- Sidebar nav ---
    'nav.dashboard': 'Panel',
    'nav.section.camp': 'CAMPAMENTO',
    'nav.section.program': 'PROGRAMA',
    'nav.section.org': 'ORGANIZACIÓN',
    'nav.section.admin': 'ADMIN',
    'nav.news': 'Noticias',
    'nav.participants': 'Participantes',
    'nav.teams': 'Equipos',
    'nav.attendance': 'Asistencia',
    'nav.checkin': 'Check-In',
    'nav.tournament': 'Torneo',
    'nav.schedule': 'Horario',
    'nav.evenings': 'Noches',
    'nav.teamtimes': 'T. de Equipo',
    'nav.questions': 'Preg. del Día',
    'nav.staff': 'Personal',
    'nav.todos': 'Tareas',
    'nav.lists': 'Listas',
    'nav.files': 'Archivos',
    'nav.meals': 'Cocina',
    'nav.finance': 'Finanzas',
    'nav.equipment': 'Equipamiento',
    'nav.downloads': 'Descargas',
    'nav.statistics': 'Estadísticas',
    'nav.forms': 'Formularios',
    'nav.emails': 'Email',
    'nav.camp_settings': 'Configuración',
    'nav.to_public': 'Sitio público',
    'nav.logout': 'Cerrar sesión',
    'nav.expand': 'Expandir',
    'nav.collapse': 'Colapsar',
    'nav.public_label': 'Público',
    'nav.intern_area': 'Área interna',

    // --- Layout / App shell strings ---
    'layout.active': 'Activo',
    'layout.archive': 'Archivo',
    'layout.archive_banner': 'Vista de archivo — {name} no es el campamento activo. No se modificarán datos.',
    'layout.to_active_camp': 'Ir al campamento activo →',
    'layout.simulation': 'Simulación',
    'layout.simulation_banner': 'Simulación: {name}',
    'layout.end_simulation': 'Terminar simulación',
    'layout.camp_not_found': 'Campamento no encontrado',
    'layout.loading': 'Cargando...',
    'layout.menu': 'Menú',
    'layout.to_platform': 'A la Plataforma',
    'layout.to_camp': 'Página del campamento',
    'layout.login': 'Iniciar sesión',
    'layout.intern': 'Personal',
    'layout.datenschutz': 'Privacidad',

    // --- Public nav ---
    'pub.home': 'Inicio',
    'pub.news': 'Noticias',
    'pub.teams': 'Equipos',
    'pub.tournament': 'Torneo',
    'pub.evenings': 'Noches',
    'pub.registration': 'Registro',
    'pub.downloads': 'Descargas',

    // --- Auth ---
    'auth.login_title': 'Iniciar sesión',
    'auth.username_or_email': 'Usuario o correo electrónico',
    'auth.password': 'Contraseña',
    'auth.login_btn': 'Iniciar sesión',
    'auth.logging_in': 'Iniciando sesión...',
    'auth.login_failed': 'Error al iniciar sesión',
    'auth.logout': 'Cerrar sesión',
    'auth.camp_login': 'Login del campamento',
    'auth.back_to_site': 'Volver al sitio del campamento',
    'auth.password_hint': 'Contraseña (dejar vacío = sin cambios)',
    'auth.register_link': '¿Sin cuenta? Regístrate ahora',
    'auth.register_title': 'Solicitar acceso',
    'auth.register_subtitle': 'Tu cuenta debe ser aprobada por la dirección del campamento.',
    'auth.display_name': 'Nombre / Alias',
    'auth.register_message': 'Mensaje para la dirección (opcional)',
    'auth.register_btn': 'Enviar solicitud',
    'auth.registering': 'Enviando...',
    'auth.register_success_title': 'Solicitud enviada',
    'auth.register_success_body': 'La dirección revisará y activará tu cuenta.',
    'auth.back_to_login': 'Volver al inicio de sesión',
    'auth.pending_approval': 'Tu cuenta aún necesita ser activada por la dirección del campamento.',
    'profile.title': 'Mi Perfil',
    'profile.account_info': 'Información de la cuenta',
    'profile.display_name': 'Nombre visible',
    'profile.username': 'Usuario',
    'profile.email': 'Correo electrónico',
    'profile.role': 'Rol',
    'profile.member_since': 'Miembro desde',
    'profile.save': 'Guardar cambios',
    'profile.saving': 'Guardando...',
    'profile.saved': 'Guardado',
    'profile.change_password': 'Cambiar contraseña',
    'profile.current_password': 'Contraseña actual',
    'profile.new_password': 'Nueva contraseña',
    'profile.confirm_password': 'Confirmar contraseña',
    'profile.password_mismatch': 'Las contraseñas no coinciden',
    'profile.password_min': 'Mínimo 8 caracteres',
    'profile.update_password': 'Actualizar contraseña',
    'profile.updating': 'Actualizando...',
    'profile.updated': 'Contraseña actualizada',

    // --- Dashboard ---
    'dashboard.to_camp_site': 'Al Sitio del Camp',
    'dashboard.participants': 'Participantes',
    'dashboard.checked_in': 'Registrados',
    'dashboard.teams': 'Equipos',
    'dashboard.paid': 'Pagado',
    'dashboard.att_today': 'Presentes hoy',
    'dashboard.open_todos': 'Tareas pendientes',
    'dashboard.settings': 'Configuración',

    // --- News ---
    'news.title': 'Noticias',
    'news.subtitle': 'Información actual, reportes y anuncios sobre el campamento.',
    'news.create': '+ Crear publicación',
    'news.edit': 'Editar publicación',
    'news.new': 'Nueva publicación',
    'news.no_news': 'Aún no hay noticias',
    'news.no_news_desc': 'Vuelve más tarde.',
    'news.read_more': 'Leer más',
    'news.read': 'Leer',
    'news.pinned': 'FIJADO',
    'news.featured': 'DESTACADO',
    'news.by': 'por',
    'news.images': 'Imágenes',
    'news.more_news': 'Más noticias',
    'news.back': 'Volver a noticias',
    'news.not_found': 'Artículo no encontrado',
    'news.not_found_desc': 'Esta publicación no existe o ha sido archivada.',
    'news.share': 'Compartir:',
    'news.copy_link': 'Copiar enlace',
    'news.copied': '¡Copiado!',
    'news.article_count': '{count} publicaciones en total',
    'news.filter_all': 'Todas',
    'news.pin': 'Fijar',
    'news.unpin': 'Desfijar',
    'news.set_featured': 'Establecer como destacado',
    'news.set_homepage': 'Mostrar en inicio',
    'news.slug_label': 'URL Slug',
    'news.slug_hint': 'Generado automáticamente',
    'news.teaser': 'Adelanto / Texto de vista previa',
    'news.content': 'Contenido',
    'news.hero_image': 'Imagen principal',
    'news.gallery': 'Galería de imágenes',
    'news.gallery_hint': 'Arrastra y suelta para reordenar imágenes',
    'news.seo_title': 'Título SEO',
    'news.seo_description': 'Descripción SEO',
    'news.tags': 'Etiquetas',
    'news.publish_at': 'Publicar el',
    'news.expires_at': 'Fecha de expiración',
    'news.duplicate': 'Duplicar',
    'news.status_change': 'Cambiar estado',
    'news.delete_confirm': '¿Realmente eliminar esta publicación?',
    'news.lang_tab_hint': 'Cambia el idioma de los campos de texto. Las imágenes y metadatos son iguales en todos los idiomas.',

    // News status
    'news.status.draft': 'Borrador',
    'news.status.scheduled': 'Programado',
    'news.status.published': 'Publicado',
    'news.status.archived': 'Archivado',

    // News categories
    'news.cat.general': 'General',
    'news.cat.registration': 'Registro',
    'news.cat.tournament': 'Torneo',
    'news.cat.program': 'Programa',
    'news.cat.teams': 'Equipos',
    'news.cat.staff': 'Personal',
    'news.cat.parents': 'Padres',
    'news.cat.reminder': 'Recordatorio',
    'news.cat.recap': 'Resumen',
    'news.cat.important': 'Importante',

    // News admin
    'news.admin.title': 'Gestionar noticias',
    'news.admin.all': 'Todas',
    'news.admin.drafts': 'Borradores',
    'news.admin.published': 'Publicadas',
    'news.admin.scheduled': 'Programadas',
    'news.admin.archived': 'Archivadas',
    'news.admin.no_results': 'No se encontraron publicaciones',
    'news.admin.search': 'Buscar publicaciones...',

    // --- Participants ---
    'participants.title': 'Participantes',
    'participants.add': '+ Participante',
    'participants.search': 'Buscar participantes...',
    'participants.import_csv': '📤 Importar CSV',
    'participants.first_name': 'Nombre',
    'participants.last_name': 'Apellido',
    'participants.jersey': 'Número de camiseta',
    'participants.age': 'Edad',
    'participants.team': 'Equipo',
    'participants.all_teams': 'Todos los equipos',
    'participants.position': 'Posición',
    'participants.experience': 'Experiencia',
    'participants.gender': 'Género',
    'participants.notes': 'Notas',
    'participants.edit': 'Editar participante',
    'participants.new': 'Nuevo participante',
    'participants.delete_confirm': '¿Realmente eliminar este participante?',
    'participants.no_results': 'No se encontraron participantes',
    'participants.total': '{count} participantes',
    'participants.payment': 'Pago',
    'participants.registration_status': 'Estado de registro',
    'participants.diet': 'Dieta',
    'participants.count': '{filtered} de {total} participantes',
    'participants.add_btn': 'Agregar',
    'participants.set_status': 'Establecer estado',
    'participants.set_payment': 'Establecer pago',
    'participants.assign_team': 'Asignar equipo',
    'participants.no_team': 'Sin equipo',
    'participants.grid_edit': 'Editar cuadrícula',
    'participants.end_grid_edit': 'Finalizar edición',
    'participants.selected': '{n} seleccionados',
    'participants.actions': 'Acciones',
    'participants.page_of': 'Página {page} de {total}',
    'participants.save_view': 'Guardar vista',
    'participants.view_name': 'Nombre de la vista',
    'participants.add_modal_title': 'Agregar participante',
    'participants.personal_data': 'Datos personales',
    'participants.baseball': 'Béisbol',
    'participants.contact': 'Contacto',
    'participants.health': 'Salud',
    'participants.status_payment': 'Estado y pago',
    'participants.internal_notes': 'Notas internas',
    'participants.notes_placeholder': 'Solo visible para el personal…',
    'participants.first_name_label': 'Nombre',
    'participants.last_name_label': 'Apellido',
    'participants.email': 'Correo electrónico',
    'participants.phone': 'Teléfono',
    'participants.dob': 'Fecha de nacimiento',
    'participants.shirt': 'Camiseta',
    'participants.club': 'Club',
    'participants.play_position': 'Posición de juego',
    'participants.level': 'Nivel',
    'participants.height': 'Estatura (cm)',
    'participants.weight': 'Peso (kg)',
    'participants.emergency_contact': 'Nombre contacto de emergencia',
    'participants.emergency_phone': 'Tel. de emergencia',
    'participants.allergies': 'Alergias',
    'participants.nutrition': 'Alimentación',
    'participants.medical': 'Notas médicas',
    'participants.parent_name': 'Nombre del padre/madre',
    'participants.parent_phone': 'Tel. del padre/madre',
    'participants.parent_email': 'Email del padre/madre',
    'participants.amount': 'Importe (€)',
    'participants.amount_paid': 'Pagado (€)',
    'participants.date': 'Fecha',
    'participants.delete_confirm_bulk': '¿Realmente eliminar {n} participantes?',
    'participants.delete_confirm_single': '¿Realmente eliminar este participante?',
    'participants.status.new': 'Nuevo registro',
    'participants.status.incomplete': 'Incompleto',
    'participants.status.confirmed': 'Confirmado',
    'participants.status.waitlist': 'Lista de espera',
    'participants.status.cancelled': 'Cancelado',
    'participants.status.checked_in': 'Registrado',
    'participants.status.departed': 'Salió',
    'participants.payment.open': 'Pendiente',
    'participants.payment.partial': 'Pago parcial',
    'participants.payment.paid': 'Pagado',
    'participants.payment.discounted': 'Con descuento',
    'participants.payment.free': 'Cupo gratuito',
    'participants.payment.cash': 'Pago en efectivo',
    'participants.payment.transfer': 'Transferido',
    'participants.gender.male': 'Masculino',
    'participants.gender.female': 'Femenino',
    'participants.gender.other': 'Otro',
    'participants.exp.beginner': 'Principiante',
    'participants.exp.intermediate': 'Intermedio',
    'participants.exp.advanced': 'Avanzado+',
    'participants.exp.pro': 'Profesional',

    // Registration status
    'reg.status.registered': 'Registrado',
    'reg.status.confirmed': 'Confirmado',
    'reg.status.waitlist': 'Lista de espera',
    'reg.status.cancelled': 'Cancelado',

    // Payment
    'payment.open': 'Pendiente',
    'payment.paid': 'Pagado',
    'payment.partial': 'Parcial',
    'payment.refunded': 'Reembolsado',

    // Gender
    'gender.male': 'Masculino',
    'gender.female': 'Femenino',
    'gender.diverse': 'Diverso',
    'gender.unknown': 'Desconocido',

    // Experience
    'exp.beginner': 'Principiante',
    'exp.intermediate': 'Intermedio',
    'exp.advanced': 'Avanzado',
    'exp.elite': 'Élite',

    // --- Teams ---
    'teams.title': 'Equipos',
    'teams.add': '+ Agregar equipo',
    'teams.new': 'Nuevo equipo',
    'teams.edit': 'Editar equipo',
    'teams.name': 'Nombre del equipo',
    'teams.color': 'Color del equipo',
    'teams.timeslot': 'Franja horaria',
    'teams.logo': 'Logo',
    'teams.photo': 'Foto del equipo',
    'teams.photo_optional': 'Foto del equipo (opcional)',
    'teams.description': 'Descripción (opcional)',
    'teams.no_teams': 'No hay equipos disponibles',
    'teams.coach': 'Entrenador',
    'teams.players': 'Jugadores',
    'teams.no_players': 'No hay jugadores asignados',
    'teams.morning': 'Mañana',
    'teams.afternoon': 'Tarde',
    'teams.cheerleader': 'Animadora',
    'teams.players_tab': 'Jugadores',
    'teams.attendance_tab': 'Asistencia',
    'teams.delete_confirm': '¿Realmente eliminar este equipo?',
    'teams.delete_confirm_full': '¿Realmente eliminar este equipo? Los participantes quedarán sin asignar.',
    'teams.subtitle': '{count} Equipos · {participants} Participantes',
    'teams.tab_manage': 'Equipos',
    'teams.tab_assign': 'Asignación',
    'teams.tab_balance': 'Balance',
    'teams.create_btn': 'Crear Equipo',
    'teams.none_yet': 'Aún no hay equipos',
    'teams.none_yet_desc': '¡Crea el primer equipo!',
    'teams.avg_age': 'Edad Prom.',
    'teams.avg_height': 'Alt. Prom.',
    'teams.coaches': 'Entrenadores',
    'teams.error_load': 'Error al cargar',
    'teams.error_save': 'Error al guardar',
    'teams.modal_create': 'Crear Equipo',
    'teams.modal_edit': 'Editar Equipo',
    'teams.photo_hero': 'Foto de Equipo (Hero)',
    'teams.search_coach': 'Buscar persona…',
    'teams.no_coach_result': 'Sin resultados',
    'teams.auto_assign': 'Asignación Automática',
    'teams.save_changes': 'Guardar cambios',
    'teams.create_first': 'Primero crea equipos en la pestaña "Equipos".',
    'teams.no_team_label': 'Sin Equipo',
    'teams.search_participant': 'Buscar participante…',
    'teams.all_assigned': 'Todos asignados',
    'teams.no_results_search': 'Sin coincidencias',
    'teams.drop_hint': 'Arrastra participantes aquí para asignar',
    'teams.no_data': '(sin datos)',
    'teams.calc': 'Calculando…',
    'teams.distribute': 'Distribuyendo…',
    'teams.distribute_n': 'Asignar {n} participantes',
    'teams.no_teams_yet': 'Aún no hay equipos.',
    'teams.slot_morning': 'Mañana',
    'teams.slot_afternoon': 'Tarde',
    'teams.unassigned_warning': '{n} participantes aún sin asignar a un equipo.',
    'teams.criterion_age': 'Edad',
    'teams.criterion_height': 'Altura',
    'teams.criterion_weight': 'Peso',
    'teams.criterion_experience': 'Experiencia',
    'teams.criterion_gender': 'Género',
    'teams.auto_desc': 'Distribuir {n} participantes en {teams} equipos. Elige criterios para una distribución equilibrada (Snake Draft):',
    'teams.tn': 'P',
    'teams.no_members': 'No hay jugadores en este equipo',
    'teams.col_year': 'Año',
    'teams.col_shirt': 'Camiseta',
    'teams.choose_image': 'Elegir imagen',
    'teams.logo_upload': 'Logo',

    // --- Attendance ---
    'att.title': 'Asistencia',
    'att.present': 'Presente',
    'att.absent': 'Ausente',
    'att.late': 'Tardanza',
    'att.excused': 'Justificado',
    'att.not_recorded': '— No registrado',
    'att.click_hint': 'Haz clic en el estado para cambiarlo',
    'att.date': 'Fecha',
    'att.all_present': 'Todos presentes',
    'att.overview': 'Resumen',

    // --- Check-in ---
    'checkin.title': 'Check-In',
    'checkin.search': 'Buscar participante...',
    'checkin.checked_in': 'Con check-in',
    'checkin.not_checked_in': 'Sin check-in',
    'checkin.all': 'Todos',
    'checkin.check_in': 'Check-In',
    'checkin.check_out': 'Check-Out',
    'checkin.no_results': 'Sin resultados',
    'checkin.total': '{checked}/{total} con check-in',

    // --- Tournament ---
    'tourn.title': 'Torneo',
    'tourn.add_match': '+ Agregar partido',
    'tourn.no_matches': 'Sin calendario de torneo',
    'tourn.live_matches': 'PARTIDOS EN VIVO',
    'tourn.scheduled': 'Programado',
    'tourn.live': 'EN VIVO',
    'tourn.finished': 'Finalizado',
    'tourn.field': 'Campo',
    'tourn.round': 'Ronda',
    'tourn.edit_score': 'Editar marcador',
    'tourn.live_score': 'Marcador en vivo',
    'tourn.score_status': 'Marcador / Estado',
    'tourn.add_match_title': 'Agregar partido',
    'tourn.edit_match_title': 'Editar partido',
    'tourn.team1': 'Equipo 1',
    'tourn.team2': 'Equipo 2',
    'tourn.tbd': '— Por definir —',
    'tourn.sort_order': 'Orden',
    'tourn.delete_confirm': '¿Realmente eliminar este partido?',
    'tourn.vs': 'vs.',
    'tourn.live_ticker': 'Ticker en vivo',
    'tourn.no_matches_title': 'Sin partidos aún',
    'tourn.no_matches_desc': 'Genera un round-robin o agrega partidos individuales.',
    'tourn.generate_btn': 'Generar Round-Robin',
    'tourn.match_short': 'Partido',
    'tourn.generating': 'Generando…',
    'tourn.generate_confirm': '¿Eliminar todos los partidos existentes y regenerar el round-robin?',
    'tourn.generated_msg': '{count} partidos en {rounds} rondas generados.',
    'tourn.generate_error': 'Error al generar',
    'tourn.tab_standings': 'Tabla',
    'tourn.tab_schedule': 'Calendario',
    'tourn.tab_bracket': 'Bracket',
    'tourn.tab_results': 'Resultados',
    'tourn.no_matches_empty': 'Sin partidos',
    'tourn.no_results': 'Sin resultados aún',
    'tourn.round_n': 'Ronda {n}',
    'tourn.matches_count': '{n} partidos',
    'tourn.col_games': 'P',
    'tourn.col_wins': 'V',
    'tourn.col_ties': 'E',
    'tourn.col_losses': 'D',
    'tourn.col_points': 'Puntos',
    'tourn.field_n': 'Campo {n}',
    'tourn.ago_now': 'ahora mismo',
    'tourn.ago_min': 'hace {n}m',
    'tourn.ago_hour': 'hace {n}h',
    'tourn.home_team': 'Equipo local',
    'tourn.away_team': 'Equipo visitante',
    'tourn.select_team': 'Elegir equipo…',
    'tourn.kickoff': 'Hora de inicio',
    'tourn.new_match': 'Nuevo partido',
    'tourn.live_end': 'Terminar en vivo',
    'tourn.live_start': 'Marcar como en vivo',
    'tourn.stat_teams': 'Equipos',
    'tourn.stat_matches': 'Partidos',
    'tourn.delete_match_confirm': '¿Eliminar partido?',

    // --- Schedule ---
    'schedule.title': 'Horario diario',
    'schedule.add_item': '+ Agregar elemento',
    'schedule.no_schedule': 'Aún no hay horario',
    'schedule.time': 'Hora',
    'schedule.activity': 'Actividad',
    'schedule.view': '→ Ver horario',
    'schedule.delete_confirm': '¿Realmente eliminar este elemento?',
    'schedule.add_entry': 'Agregar entrada',
    'schedule.add_day': 'Día',
    'schedule.no_days': 'Aún no hay días',
    'schedule.no_days_desc': 'Agrega un día con "+ Día".',
    'schedule.no_entries': 'Sin entradas para este día',
    'schedule.no_entries_desc': 'Agrega el primer elemento del programa.',
    'schedule.edit_entry': 'Editar entrada',
    'schedule.new_entry': 'Nueva entrada',
    'schedule.type_entry': 'Punto del programa',
    'schedule.type_header': 'Encabezado de sección',
    'schedule.type_divider': 'Separador',
    'schedule.color': 'Color',
    'schedule.start_time': 'Hora de inicio',
    'schedule.duration_min': 'Duración (minutos)',
    'schedule.location': 'Lugar',
    'schedule.responsible': 'Responsable',
    'schedule.delete_title': 'Eliminar entrada',
    'schedule.delete_msg': '¿Realmente eliminar esta entrada?',
    'schedule.delete_day_confirm': '¿Eliminar este día y todas sus entradas?',

    // --- Evenings ---
    'evenings.title': 'Noches del campamento',
    'evenings.add': '+ Agregar noche',
    'evenings.no_evenings': 'No hay noches planificadas',
    'evenings.no_evenings_desc': 'Los programas nocturnos aparecerán aquí.',
    'evenings.edit': 'Editar noche',
    'evenings.new': 'Nueva noche',
    'evenings.date': 'Fecha',
    'evenings.schedule': 'Horario',
    'evenings.add_schedule_item': '+ Agregar elemento',
    'evenings.no_schedule': 'Aún no hay horario',
    'evenings.view_schedule': '→ Ver horario',
    'evenings.delete_confirm': '¿Realmente eliminar esta noche?',
    'evenings.edit_evening': 'Editar noche',
    'evenings.evening_name': 'Nombre de la noche',
    'evenings.banner': 'Imagen de portada',
    'evenings.upload_image': 'Subir imagen',
    'evenings.replace_image': 'Reemplazar imagen',
    'evenings.uploading': 'Subiendo…',

    // --- Teamtimes ---
    'teamtimes.title': 'Tiempo de Equipo',
    'teamtimes.add': '+ Agregar tiempo de equipo',
    'teamtimes.no_items': 'No hay tiempos de equipo',
    'teamtimes.edit': 'Editar tiempo de equipo',
    'teamtimes.new': 'Nuevo tiempo de equipo',
    'teamtimes.topic': 'Tema',
    'teamtimes.team': 'Equipo',
    'teamtimes.delete_confirm': '¿Realmente eliminar este tiempo de equipo?',
    'teamtimes.field_title': 'Título',
    'teamtimes.field_date': 'Fecha',
    'teamtimes.field_audience': 'Público objetivo',
    'teamtimes.field_theme': 'Tema',
    'teamtimes.field_bible': 'Referencia bíblica',
    'teamtimes.field_content': 'Contenido',
    'teamtimes.field_questions': 'Preguntas de discusión',
    'teamtimes.field_staff': 'Notas del personal (interno)',
    'teamtimes.section_content': 'Contenido',
    'teamtimes.section_questions': 'Preguntas de discusión',
    'teamtimes.section_staff': 'Notas del personal',
    'teamtimes.attachments': 'Adjuntos',
    'teamtimes.no_attachments': 'Sin adjuntos',
    'teamtimes.attach_file': 'Adjuntar archivo',
    'teamtimes.uploading': 'Subiendo…',
    'teamtimes.theme_prefix': 'Tema:',

    // --- Questions ---
    'questions.title': 'Pregunta del día',
    'questions.add': '+ Agregar pregunta',
    'questions.no_items': 'No hay preguntas',
    'questions.edit': 'Editar pregunta',
    'questions.new': 'Nueva pregunta',
    'questions.question': 'Pregunta',
    'questions.date': 'Fecha',
    'questions.delete_confirm': '¿Realmente eliminar esta pregunta?',

    // --- Staff ---
    'staff.title': 'Personal',
    'staff.add': '+ Agregar personal',
    'staff.no_staff': 'No hay personal',
    'staff.edit': 'Editar personal',
    'staff.new': 'Nuevo miembro del personal',
    'staff.role': 'Rol',
    'staff.team': 'Equipo',
    'staff.delete_confirm': '¿Realmente eliminar este miembro?',
    'staff.delete_title': 'Eliminar personal',
    'staff.delete_msg': '¿Realmente desea eliminar este miembro del personal?',
    'staff.name': 'Nombre',
    'staff.phone': 'Teléfono',
    'staff.special_tasks': 'Tareas especiales',
    'staff.availability': 'Disponibilidad / Notas',
    'staff.count': '{filtered} de {total} personas',
    'staff.not_found': 'No se encontró personal',
    'staff.no_staff_yet': 'Aún no hay personal',
    'staff.no_staff_yet_desc': 'Agrega el primer miembro del personal.',
    'staff.grid_edit': 'Editar cuadrícula',
    'staff.end_grid_edit': 'Finalizar edición',
    'staff.new_member_ghost': 'Nuevo miembro',

    // --- ToDos ---
    // --- Checkin ---
    'checkin.title': 'Check-In',
    'checkin.checked_in_of': '{checkedIn} de {total} registrados',
    'checkin.total': 'Total',
    'checkin.checked_in': 'Registrados',
    'checkin.pending': 'Pendientes',
    'checkin.checked_in_label': '{n} registrados',
    'checkin.search': 'Buscar nombre…',
    'checkin.no_results': 'Nada encontrado',
    'checkin.no_results_search': 'Sin resultados para "{q}"',
    'checkin.no_participants': 'Aún no hay participantes',
    'checkin.undo': 'Deshacer',
    'checkin.check_in_btn': 'Registrar',

    // --- Attendance ---
    'attendance.title': 'Asistencia',
    'attendance.present_of': '{present} / {total} presentes',
    'attendance.saved_at': 'Guardado {time}',
    'attendance.all_present': 'Todos presentes',
    'attendance.all_absent': 'Todos ausentes',
    'attendance.present': 'Presente',
    'attendance.absent': 'Ausente',
    'attendance.none': 'Ninguno',
    'attendance.no_team': 'Sin equipo',
    'attendance.no_participants': 'No se encontraron participantes',

    'todos.title': 'Tareas',
    'todos.add': '+ Agregar tarea',
    'todos.no_todos': 'No hay tareas',
    'todos.open': 'Abiertas',
    'todos.done': 'Completadas',
    'todos.all': 'Todas',
    'todos.new': 'Nueva tarea',
    'todos.edit': 'Editar tarea',
    'todos.task': 'Tarea',
    'todos.assignee': 'Responsable',
    'todos.due_date': 'Fecha límite',
    'todos.priority': 'Prioridad',
    'todos.delete_confirm': '¿Realmente eliminar esta tarea?',
    'todos.open_count': '{count} abiertas',
    'todos.stat_line': '{open} abiertas · {done} completadas',
    'todos.no_open': 'Sin tareas pendientes',
    'todos.nothing_done': 'Nada completado aún',
    'todos.title_label': 'Título',
    'todos.description': 'Descripción',
    'todos.due': 'Vence',
    'todos.assigned_to': 'Asignado a',
    'todos.prio_high': 'Alta',
    'todos.prio_medium': 'Media',
    'todos.prio_low': 'Baja',
    'todos.prio_normal': 'Normal',
    'todos.prio_urgent': 'Urgente',

    // --- Kanban Board ---
    'todos.boards': 'Tableros',
    'todos.new_board': 'Nuevo Tablero',
    'todos.no_boards': 'Sin tableros',
    'todos.no_boards_yet': 'Crea tu primer tablero para comenzar.',
    'todos.create_first_board': 'Crear Primer Tablero',
    'todos.board_name': 'Nombre del Tablero',
    'todos.board_desc': 'Descripción (opcional)',
    'todos.board_settings': 'Configuración del Tablero',
    'todos.add_list': '+ Agregar Lista',
    'todos.add_card': '+ Agregar Tarjeta',
    'todos.list_name': 'Nombre de la Lista',
    'todos.card_title': 'Título de la Tarjeta',
    'todos.view_kanban': 'Kanban',
    'todos.view_list': 'Tabla',
    'todos.view_calendar': 'Calendario',
    'todos.view_my_tasks': 'Mis Tareas',
    'todos.labels': 'Etiquetas',
    'todos.members': 'Miembros',
    'todos.start_date': 'Fecha de Inicio',
    'todos.cover_color': 'Color de Portada',
    'todos.checklist': 'Lista de Verificación',
    'todos.add_checklist': '+ Agregar Lista',
    'todos.checklist_name': 'Nombre de la Lista',
    'todos.add_item': '+ Agregar Elemento',
    'todos.checklist_progress': '{done} / {total} completados',
    'todos.comments': 'Comentarios',
    'todos.write_comment': 'Escribe un comentario...',
    'todos.activity': 'Actividad',
    'todos.move_to_list': 'Mover a Lista',
    'todos.archive_card': 'Archivar Tarjeta',
    'todos.archive_board': 'Archivar Tablero',
    'todos.templates': 'Plantillas',
    'todos.save_as_template': 'Guardar como Plantilla',
    'todos.load_template': 'Cargar Plantilla',
    'todos.filter_assignee': 'Miembro',
    'todos.filter_priority': 'Prioridad',
    'todos.filter_label': 'Etiqueta',
    'todos.filter_due': 'Vencimiento',
    'todos.my_tasks': 'Mis Tareas',
    'todos.overdue': 'Vencida',
    'todos.today': 'Hoy',
    'todos.upcoming': 'Próximamente',
    'todos.no_date': 'Sin Fecha',
    'todos.today_section': 'Vence Hoy',
    'todos.overdue_section': 'Vencidas',
    'todos.upcoming_section': 'Próximamente',
    'todos.no_cards': 'Sin Tarjetas',
    'todos.no_my_tasks': 'No tienes tareas asignadas.',
    'todos.card_archived': 'Tarjeta archivada',
    'todos.board_archived': 'Tablero archivado',
    'todos.list_archived': 'Lista archivada',
    'todos.duplicate_board': 'Duplicar Tablero',
    'todos.rename_list': 'Renombrar Lista',
    'todos.delete_list': 'Archivar Lista',
    'todos.mark_done_list': 'Marcar como Lista de Completados',
    'todos.activity_created': 'creó esta tarjeta',
    'todos.activity_moved': 'movió esta tarjeta',
    'todos.activity_commented': 'comentó',
    'todos.activity_archived': 'archivó esta tarjeta',
    'todos.activity_assigned': 'cambió miembros',
    'todos.activity_due_date_changed': 'cambió la fecha de vencimiento',
    'todos.activity_label_changed': 'cambió etiquetas',
    'todos.no_description': 'Sin descripción.',
    'todos.edit_description': 'Editar descripción...',
    'todos.cover_none': 'Sin Portada',
    'todos.export_csv': 'Exportar como CSV',
    'todos.export_xlsx': 'Exportar como Excel',
    'todos.template_name': 'Nombre de Plantilla',
    'todos.apply_template': 'Aplicar Plantilla',
    'todos.done_list_badge': 'Lista de Completados',
    'todos.list_has_cards': 'Esta lista aún tiene tarjetas activas.',

    // --- Lists ---
    'lists.title': 'Listas',
    'lists.add': '+ Agregar lista',
    'lists.no_lists': 'No hay listas',
    'lists.no_lists_hint': 'Sin listas aún.\nHaz clic en "Nueva lista".',
    'lists.edit': 'Editar lista',
    'lists.new': 'Nueva lista',
    'lists.new_short': 'Nuevo',
    'lists.delete_confirm': '¿Realmente eliminar esta lista?',
    'lists.delete_list_title': 'Eliminar lista',
    'lists.delete_list_msg': 'Esta lista y todas las filas se eliminarán permanentemente.',
    'lists.delete_row_title': 'Eliminar fila',
    'lists.delete_row_msg': 'Esta fila se eliminará permanentemente.',
    'lists.add_item': '+ Elemento',
    'lists.items': 'Elementos',
    'lists.entries': 'entradas',
    'lists.of': 'de',
    'lists.select_list': 'Seleccionar lista',
    'lists.select_list_desc': 'Selecciona una lista a la izquierda o crea una nueva.',
    'lists.select_mobile': 'Elegir lista',
    'lists.add_row': 'Agregar',
    'lists.new_row': 'Nueva fila',
    'lists.edit_row': 'Editar fila',
    'lists.columns_btn': 'Columnas',
    'lists.manage_columns': 'Administrar columnas',
    'lists.no_columns': 'Sin columnas definidas',
    'lists.no_columns_desc': 'Haz clic en "Columnas" para agregar columnas.',
    'lists.grid_done': 'Listo',
    'lists.grid_mode': 'Cuadrícula',
    'lists.selected_n': '{n} seleccionados',
    'lists.empty_rows': 'Sin entradas — haz clic en "+ Agregar"',
    'lists.no_results_search': 'Sin resultados',
    'lists.template': 'Usar plantilla',
    'lists.template_empty': 'En blanco (sin plantilla)',
    'lists.row_no_columns': 'Sin columnas definidas.',
    'lists.no_options': 'Sin opciones definidas — agrega en la configuración de columnas.',
    'lists.person_placeholder': 'Buscar persona…',
    'lists.no_selection': '— sin selección —',
    'lists.icon': 'Ícono',

    // --- Files ---
    'files.title': 'Archivos',
    'files.upload': '+ Subir archivo',
    'files.no_files': 'No hay archivos',
    'files.delete_confirm': '¿Realmente eliminar este archivo?',
    'files.folder': 'Carpeta',
    'files.new_folder': 'Nueva carpeta',
    'files.file_name': 'Nombre del archivo',
    'files.size': 'Tamaño',
    'files.uploaded_at': 'Subido el',
    'files.file': 'Archivo',
    'files.files': 'Archivos',
    'files.upload_btn': 'Subir',
    'files.upload_error': 'Error al subir. Por favor intenta de nuevo.',
    'files.drop_hint': 'Arrastra archivos aquí para subir · máx. 50 MB por archivo',
    'files.select_btn': 'Seleccionar',
    'files.no_results': 'Sin resultados',
    'files.no_results_for': 'Sin resultados para',
    'files.empty_desc': 'Arrastra archivos aquí o haz clic en "Subir"',
    'files.rename_folder': 'Renombrar carpeta',
    'files.new_name': 'Nuevo nombre',
    'files.delete_file_title': 'Eliminar archivo',
    'files.delete_file_msg': 'Este archivo se eliminará permanentemente.',
    'files.delete_folder_title': 'Eliminar carpeta',
    'files.delete_folder_msg': 'Esta carpeta y todo su contenido se eliminará permanentemente.',
    'files.items_count': 'Elementos',
    'files.items_short': 'Elem.',
    'files.no_entries': 'No se encontraron entradas',
    'files.selected': 'seleccionados',
    'files.list_view': 'Vista de lista',
    'files.grid_view': 'Vista de cuadrícula',
    'files.col_type': 'Tipo',
    'files.folder_type': 'Carpeta',

    // --- Meals ---
    'meals.title': 'Cocina y Comidas',
    'meals.add': '+ Comida',
    'meals.no_meals': 'Aún no hay comidas',
    'meals.edit': 'Editar comida',
    'meals.new': 'Nueva comida',
    'meals.meal': 'Comida',
    'meals.date': 'Fecha',
    'meals.portions': 'Porciones',
    'meals.delete_confirm': '¿Eliminar comida?',
    'meals.shopping_list': 'Lista de compras',
    'meals.no_date': 'Sin fecha',
    'meals.field_title': 'Título',
    'meals.field_type': 'Tipo',
    'meals.notes_ingredients': 'Notas / Ingredientes',
    'meals.type.breakfast': 'Desayuno',
    'meals.type.lunch': 'Almuerzo',
    'meals.type.dinner': 'Cena',
    'meals.type.snack': 'Merienda',

    // --- Finance ---
    'finance.title': 'Finanzas',
    'finance.add': '+ Agregar entrada',
    'finance.no_items': 'No hay entradas',
    'finance.edit': 'Editar entrada',
    'finance.new': 'Nueva entrada',
    'finance.income': 'Ingresos',
    'finance.expense': 'Gastos',
    'finance.balance': 'Balance',
    'finance.amount': 'Monto',
    'finance.category': 'Categoría',
    'finance.description': 'Descripción',
    'finance.date': 'Fecha',
    'finance.delete_confirm': '¿Realmente eliminar esta entrada?',

    // --- Equipment ---
    'equipment.title': 'Equipamiento',
    'equipment.add': '+ Agregar equipamiento',
    'equipment.no_items': 'No hay equipamiento',
    'equipment.edit': 'Editar equipamiento',
    'equipment.new': 'Nuevo equipamiento',
    'equipment.item': 'Artículo',
    'equipment.quantity': 'Cantidad',
    'equipment.condition': 'Estado',
    'equipment.location': 'Ubicación',
    'equipment.delete_confirm': '¿Realmente eliminar este equipamiento?',
    'equipment.col_category': 'Categoría',
    'equipment.col_notes': 'Notas',
    'equipment.cond.new': 'Nuevo',
    'equipment.cond.good': 'Bueno',
    'equipment.cond.fair': 'Regular',
    'equipment.cond.poor': 'Malo',

    // --- Downloads ---
    'downloads.title': 'Descargas',
    'downloads.add': '+ Agregar archivo',
    'downloads.no_items': 'No hay descargas',
    'downloads.edit': 'Editar entrada',
    'downloads.new': 'Nueva entrada',
    'downloads.delete_confirm': '¿Realmente eliminar esta descarga?',
    'downloads.file': 'Archivo',
    'downloads.category': 'Categoría',
    'downloads.subtitle': 'Archivos para la página de descargas pública',
    'downloads.add_file': 'Agregar archivo',
    'downloads.no_items_desc': 'Agrega volantes, formularios y otros materiales aquí.',
    'downloads.file_available': 'Archivo adjunto',
    'downloads.public': 'Público',
    'downloads.hidden': 'Oculto',
    'downloads.visibility': 'Visibilidad',
    'downloads.public_visible': 'Visible públicamente',
    'downloads.select_file': 'Seleccionar archivo',
    'downloads.edit_file': 'Editar archivo',
    'downloads.new_file': 'Nuevo archivo',
    'downloads.toggle_show': 'Público – clic para ocultar',
    'downloads.toggle_hide': 'Oculto – clic para publicar',
    'downloads.delete_file_confirm': '¿Eliminar este archivo?',

    // --- Statistics ---
    'stats.title': 'Estadísticas',
    'stats.subtitle': 'Resumen del campamento',
    'stats.participants': 'Participantes',
    'stats.checked_in': 'Con check-in',
    'stats.teams': 'Equipos',
    'stats.paid': 'Pagado',
    'stats.today_present': 'Presentes hoy',
    'stats.open_todos': 'Tareas abiertas',
    'stats.attendance_rate': 'Tasa de asistencia',
    'stats.payment_rate': 'Tasa de pago',
    'stats.progress': 'Progreso',
    'stats.att_week': 'Asistencia – últimos 7 días',
    'stats.team_sizes': 'Tamaño de equipos',
    'questions.answer_label': 'Respuesta',
    'questions.answer_placeholder': 'Ingresar respuesta...',
    'questions.answer_btn': 'Responder',
    'questions.no_questions': 'Aún no hay preguntas',

    // --- Dashboard ---
    'dash.to_camp': 'Página del campamento',
    'dash.settings': 'Configuración',

    // --- Forms ---
    'forms.title': 'Formularios',
    'forms.registration': 'Formulario de registro',
    'forms.add': '+ Agregar formulario',
    'forms.no_forms': 'No hay formularios',
    'forms.edit': 'Editar formulario',
    'forms.new': 'Nuevo formulario',
    'forms.field': 'Campo',
    'forms.label': 'Etiqueta',
    'forms.required': 'Requerido',
    'forms.delete_confirm': '¿Realmente eliminar este formulario?',
    'forms.responses': 'Respuestas',
    'forms.no_responses': 'Aún no hay respuestas',
    'forms.subtitle': 'Crea y administra formularios de registro para tu campamento',
    'forms.no_forms_title': 'Sin formularios aún',
    'forms.no_forms_desc': 'Crea tu primer formulario de registro.',
    'forms.create': 'Crear formulario',
    'forms.active': 'Activo',
    'forms.inactive': 'Inactivo',
    'forms.fields_count': '{n} campos',
    'forms.submissions_count': '{n} inscripciones',
    'forms.submissions': 'Inscripciones',
    'forms.delete_form_confirm': '¿Eliminar formulario y todos los campos?',
    'forms.load_error': 'No se pudo cargar el formulario.',
    'forms.create_error': 'Error al crear el formulario.',
    'forms.saved': 'Guardado ✓',
    'forms.save_error': '¡Error!',
    'forms.unsaved_confirm': '¿Descartar cambios sin guardar?',
    'forms.back': 'Volver',
    'forms.back_to_builder': 'Volver al editor',
    'forms.preview_label': 'Vista previa',
    'forms.saving': 'Guardando…',
    'forms.save_dirty': 'Guardar*',
    'forms.saved_btn': 'Guardado',
    'forms.description_label': 'Descripción',
    'forms.description_placeholder': 'Breve descripción del formulario (visible públicamente)',
    'forms.confirmation_label': 'Texto de confirmación',
    'forms.confirmation_placeholder': 'Texto tras registro exitoso...',
    'forms.button_color': 'Color del botón',
    'forms.add_fields': 'Agregar campos',
    'forms.add_field_btn': '+ Agregar campo',
    'forms.field_count': '{n} campos',
    'forms.canvas_empty': 'Agrega campos a la izquierda para construir tu formulario.',
    'forms.select_field': 'Selecciona un campo para editar',
    'forms.activate_title': 'Activar formulario (hacer públicamente accesible)',
    'forms.drag_hint': 'Arrastrar para reordenar',
    'forms.move_up': 'Subir',
    'forms.move_down': 'Bajar',
    'forms.name_label': 'Nombre del formulario *',
    'forms.name_placeholder': 'ej. Inscripción Baseball Camp 2026',
    'forms.submit_preview': 'Registrarse → (Vista previa)',
    'forms.select_placeholder': 'Por favor selecciona…',
    'forms.submissions_title': 'Inscripciones: {name}',
    'forms.submissions_count_msg': '{n} inscripciones recibidas',
    'forms.no_submissions': 'Sin inscripciones aún',
    'forms.no_submissions_desc': 'Una vez que alguien complete el formulario, aparecerá aquí.',
    'forms.col_name': 'Nombre',
    'forms.col_email': 'Correo electrónico',
    'forms.col_status': 'Estado',
    'forms.col_registered': 'Registrado el',
    'forms.fe_edit_section': 'Editar sección',
    'forms.fe_section_title': 'Título',
    'forms.fe_section_desc': 'Descripción (opcional)',
    'forms.fe_section_desc_placeholder': 'Breve explicación de esta sección',
    'forms.fe_delete_section': 'Eliminar sección',
    'forms.fe_edit_field': 'Editar campo',
    'forms.fe_field_type': 'Tipo de campo',
    'forms.fe_label_de': 'Etiqueta (DE) *',
    'forms.fe_label_en': 'Etiqueta (EN, opcional)',
    'forms.fe_help_text': 'Texto de ayuda',
    'forms.fe_help_placeholder': 'Se muestra debajo del campo...',
    'forms.fe_placeholder': 'Marcador de posición',
    'forms.fe_required': 'Obligatorio',
    'forms.fe_mapping': 'Mapeo → Campo de participante',
    'forms.fe_mapping_desc': 'Asigna automáticamente la respuesta al perfil del participante.',
    'forms.fe_options': 'Opciones',
    'forms.fe_option_label': 'Etiqueta',
    'forms.fe_option_value': 'Valor',
    'forms.fe_add_option': '+ Opción',
    'forms.fe_delete_field': 'Eliminar campo',
    'forms.ft_text': 'Campo de texto',
    'forms.ft_textarea': 'Texto multilínea',
    'forms.ft_email': 'Correo electrónico',
    'forms.ft_phone': 'Número de teléfono',
    'forms.ft_date': 'Fecha',
    'forms.ft_number': 'Campo numérico',
    'forms.ft_select': 'Campo de selección',
    'forms.ft_multiselect': 'Selección múltiple',
    'forms.ft_checkbox': 'Casilla de verificación',
    'forms.ft_yesno': 'Sí / No',

    // --- Camp Admin Settings ---
    'admin.title': 'Configuración del campamento',
    'admin.modules': 'Módulos',
    'admin.modules_desc': 'Habilita o deshabilita funciones para este campamento.',
    'admin.branding': 'Diseño y branding',
    'admin.general': 'General',
    'admin.users': 'Usuarios',
    'admin.users_add': '+ Crear usuario',
    'admin.users_edit': 'Editar usuario',
    'admin.users_new': 'Nuevo usuario',
    'admin.users_search': 'Buscar usuarios...',
    'admin.username': 'Nombre de usuario',
    'admin.display_name': 'Nombre para mostrar',
    'admin.email': 'Correo electrónico',
    'admin.role': 'Rol',
    'admin.roles': 'Roles',
    'admin.assign_teams': 'Asignar equipos',
    'admin.account_active': 'Cuenta activa',
    'admin.active': 'Activo',
    'admin.inactive': 'Inactivo',
    'admin.delete_user_confirm': '¿Realmente eliminar este usuario?',
    'admin.permissions': 'Permisos',
    'admin.simulate': 'Simular como este usuario',
    'admin.tab_general': 'General',
    'admin.tab_camps': 'Campamentos',
    'admin.tab_users': 'Usuarios',
    'admin.tab_permissions': 'Roles y Permisos',
    'admin.tab_branding': 'Marca y Colores',
    'admin.tab_cms': 'Página de inicio',
    'admin.tab_modules': 'Módulos',
    'admin.general_title': 'Información del Campamento',
    'admin.general_readonly': 'Los datos del campamento solo pueden ser editados por superadmins.',
    'admin.field_location': 'Ubicación',
    'admin.field_start': 'Inicio',
    'admin.field_end': 'Fin',
    'admin.camps_manage': 'Gestiona las instancias anuales de este campamento',
    'admin.camps_org': 'Organización: {name}',
    'admin.new_camp': 'Nuevo Campamento',
    'admin.no_camps': 'Aún no hay otros campamentos en este tenant',
    'admin.camp_current': 'Actual',
    'admin.camp_active': 'Activo',
    'admin.camp_activate': 'Activar',
    'admin.camp_delete_title': 'Eliminar Campamento',
    'admin.camp_delete_msg': 'Este campamento y todos los participantes asociados serán eliminados permanentemente. Esta acción no se puede deshacer.',
    'admin.slug_hint': '(identificador URL)',
    'admin.field_year': 'Año',
    'admin.creating': 'Creando…',
    'admin.create_camp': 'Crear Campamento',
    'admin.users_section_title': 'Usuarios del Campamento',
    'admin.users_desc': 'Gestiona accesos y roles para este campamento',
    'admin.no_users': 'Aún no hay usuarios en este campamento',
    'admin.password': 'Contraseña',
    'admin.remove_user_title': 'Eliminar Usuario',
    'admin.remove_user_msg': '¿Eliminar este usuario del campamento? La cuenta se conservará.',
    'admin.remove': 'Eliminar',
    'admin.roles_label': 'Roles',
    'admin.new_role_title': 'Crear Nuevo Rol',
    'admin.role_display_name': 'Nombre para mostrar',
    'admin.role_key': 'Clave de Rol',
    'admin.role_key_hint': '(solo a-z, 0-9, _)',
    'admin.create_role': 'Crear Rol',
    'admin.select_role_hint': 'Selecciona un rol para editar sus permisos.',
    'admin.role_delete_confirm': '¿Eliminar rol "{name}"?',
    'admin.branding_title': 'Logo y Tema',
    'admin.branding_desc': 'Personaliza el logo y los colores del entorno del campamento para que coincidan con tu CI. Los cambios afectan la aplicación interna, la página pública y todas las áreas del campamento.',
    'admin.logo_section': 'Logo del Campamento',
    'admin.logo_hint': 'Se usa en el encabezado y como icono de PWA. Recomendado: cuadrado, mín. 512×512px.',
    'admin.logo_upload': 'Subir logo',
    'admin.logo_change': 'Cambiar logo',
    'admin.logo_remove': 'Eliminar',
    'admin.logo_uploading': 'Subiendo…',
    'admin.live_preview': 'Vista previa en vivo',
    'admin.primary_color': 'Color Primario',
    'admin.primary_hint': 'Botones, navegación activa, acentos — el color principal de tu campamento.',
    'admin.secondary_color': 'Color Secundario',
    'admin.secondary_hint': 'Segundo color de acento para destacados y badges.',
    'admin.accent_color': 'Acento',
    'admin.accent_hint': 'Tercer color para pestañas y marcaciones especiales.',
    'admin.save_branding': 'Guardar Tema',
    'admin.saved_branding': '¡Tema guardado!',
    'admin.cms_title': 'Página Pública',
    'admin.hero_tagline': 'Tagline del Hero',
    'admin.hero_subtitle': 'Subtítulo del Hero',
    'admin.camp_type': 'Tipo de Campamento',
    'admin.age_group': 'Grupo de Edad',
    'admin.price_info': 'Información de Precio',
    'admin.save_cms': 'Guardar Página de Inicio',
    'admin.saved_cms': '¡Página de inicio guardada!',
    'admin.modules_title': 'Módulos',
    'admin.save_modules': 'Guardar Módulos',
    'admin.saved_modules': '¡Módulos guardados!',
    'admin.saved_role': '¡Rol guardado!',
    'admin.error_activate': 'Error al activar',
    'admin.error_create': 'Error al crear',
    'admin.error_simulate': 'Error al simular',
    'admin.color_reset': 'Restablecer',
    'admin.adding': 'Agregar',
    'admin.tab_push': 'Push',
    'admin.tab_registrations': 'Registros',
    'admin.reg_empty': 'Sin solicitudes de registro',
    'admin.reg_approve': 'Aprobar',
    'admin.reg_reject': 'Rechazar',
    'admin.reg_approved': 'Aprobado',
    'admin.reg_rejected': 'Rechazado',
    'admin.reg_pending': 'Pendiente',
    'admin.reg_message': 'Mensaje',
    'admin.reg_requested_at': 'Solicitado el',
    'admin.push_subscribers': 'Suscriptores',
    'admin.push_send_title': 'Enviar notificación',
    'admin.push_title_label': 'Título',
    'admin.push_title_placeholder': 'ej. Nuevo anuncio del campamento',
    'admin.push_body_label': 'Mensaje',
    'admin.push_body_placeholder': 'Mensaje que recibirán los usuarios...',
    'admin.push_url_label': 'Enlace (opcional)',
    'admin.push_url_hint': '¿A dónde debe llevar un clic en la notificación?',
    'admin.push_send_btn': 'Enviar a',
    'admin.push_sending': 'Enviando...',
    'admin.push_sent': '{sent} de {total} enviados con éxito',
    'admin.push_no_subscribers': 'Sin suscriptores — activa primero en la página de noticias',
    'admin.push_auto_title': 'Notificación automática de artículos',
    'admin.push_auto_desc': 'Se envía automáticamente cuando se publica un artículo.',
    'admin.push_auto_enabled': 'Enviar automáticamente',
    'admin.push_auto_title_tpl': 'Plantilla de título',
    'admin.push_auto_body_tpl': 'Plantilla de texto',
    'admin.push_auto_vars': 'Variables disponibles: {title}, {teaser}',
    'admin.push_auto_save': 'Guardar plantilla',
    'admin.push_auto_saved': 'Guardado',
    'admin.session_title': 'Asignación de Grupos de Edad',
    'admin.session_desc': 'Define a qué edad los participantes se asignan automáticamente a mañana o tarde.',
    'admin.morning_max_age': 'Mañana – Edad máxima',
    'admin.morning_max_age_desc': 'Los participantes hasta esta edad se asignan automáticamente a mañana.',
    'admin.afternoon_min_age': 'Tarde – Edad mínima',
    'admin.afternoon_min_age_desc': 'Los participantes desde esta edad se asignan automáticamente a tarde.',
    'admin.session_auto_note': 'La asignación ocurre automáticamente al crear un participante y puede ajustarse manualmente después.',

    // --- SuperAdmin ---
    'sa.dashboard': 'Panel',
    'sa.tenants': 'Inquilinos',
    'sa.camps': 'Campamentos',
    'sa.users': 'Usuarios',
    'sa.equipment': 'Equipamiento',
    'sa.templates': 'Plantillas',
    'sa.backups': 'Copias de seguridad',
    'sa.settings': 'Configuración',
    'sa.logout': 'Cerrar sesión',
    'sa.to_platform': 'A la plataforma',
    'sa.superadmin': 'SuperAdmin',
    'sa.platform': 'Baseball Camp Platform',
    'sa.create_backup': 'Crear copia de seguridad',
    'sa.restore': 'Restaurar',
    'sa.delete_confirm': '¿Realmente eliminar?',

    // --- Platform landing ---
    'platform.title': 'Baseball Camp Platform',
    'platform.subtitle': 'Gestión para tu campamento',
    'platform.admin_login': 'Login Admin',
    'platform.camp_login': 'Login Campamento',
    'platform.enter_slug': 'Ingresa el slug del campamento...',
    'platform.go': 'Ir',

    // --- Push notifications ---
    'push.active': 'Notificaciones push activas',
    'push.disable': 'Desactivar',
    'push.success': '¡Notificaciones activadas!',
    'push.blocked': 'Notificaciones bloqueadas — actívalas en la configuración del navegador',
    'push.enable_prompt': '¿Activar notificaciones?',
    'push.title': '¿Activar Notificaciones?',
    'push.desc': 'Recibe notificaciones al instante cuando hay novedades del campamento.',
    'push.enable': 'Activar',
    'push.not_now': 'Ahora no',
  },
};

// ---------------------------------------------------------------------------
// Core lookup function (pure, no hooks)
// ---------------------------------------------------------------------------
function lookup(key, lang, vars) {
  const val =
    translations[lang]?.[key] ??
    translations[DEFAULT_LANG]?.[key] ??
    key;
  if (!vars) return val;
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v)),
    val
  );
}

// ---------------------------------------------------------------------------
// Reactive hook — reads lang from AuthContext so components re-render on change
// ---------------------------------------------------------------------------
export function useT() {
  // Import AuthContext directly to avoid circular dependency.
  // AuthContext never imports from i18n/index.js.
  const auth = useContext(AuthContext);
  const lang = auth?.lang ?? localStorage.getItem('lang') ?? DEFAULT_LANG;
  return useCallback(
    (key, vars) => lookup(key, lang, vars),
    [lang]
  );
}

// ---------------------------------------------------------------------------
// Non-hook version — for use outside components (module-level constants, etc.)
// ---------------------------------------------------------------------------
export function getT(lang) {
  const l = lang ?? localStorage.getItem('lang') ?? DEFAULT_LANG;
  return (key, vars) => lookup(key, l, vars);
}

// ---------------------------------------------------------------------------
// Locale map for date formatting
// ---------------------------------------------------------------------------
export const LOCALE_MAP = {
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
};

export function formatDate(dateStr, lang, opts) {
  const locale = LOCALE_MAP[lang] || 'de-DE';
  return new Date(dateStr).toLocaleDateString(locale, opts);
}

export default translations;
